/**
 * allSkillDefs.ts
 *
 * Master flat list of every AI skill definition across all integrations.
 * Used by SkillPermissionsSelector in the workflow AI node editor to let
 * admins restrict which skills a particular AI node may use.
 *
 * Each entry matches the SkillDef interface from SkillControlsPanel.tsx.
 * The `group` field includes the integration prefix so groups sort correctly
 * and stay visually separated in the picker UI.
 *
 * NOTE: Keep this in sync with the AI_TOOLS arrays in each integration page.
 * When adding a new skill to an integration page, add it here too.
 */

import { SkillDef } from "./SkillControlsPanel";

const ALL_SKILL_DEFS: SkillDef[] = [
  // ── Shopify ────────────────────────────────────────────────────────────────
  {
    name: "shopify_get_customer",
    label: "Look up customer",
    group: "Shopify — Customers",
    description: "Finds a customer by email or name."
  },
  {
    name: "shopify_search_products",
    label: "Search products",
    group: "Shopify — Products",
    description: "Searches your product catalog by keyword."
  },
  {
    name: "shopify_get_order",
    label: "Get order details",
    group: "Shopify — Orders",
    description: "Fetches a specific order by ID."
  },
  {
    name: "shopify_get_recent_orders",
    label: "Recent orders for customer",
    group: "Shopify — Orders",
    description: "Returns the last 5 orders for a customer."
  },
  {
    name: "shopify_search_orders",
    label: "Search orders",
    group: "Shopify — Orders",
    description: "Search orders by customer email or order number."
  },
  {
    name: "shopify_get_abandoned_cart",
    label: "Abandoned cart recovery",
    group: "Shopify — Orders",
    description: "Finds incomplete checkouts and returns a recovery link.",
    channelWarning:
      "Returns a checkout URL. On IVR/SMS the AI can read it aloud or text it, but add a Send SMS or Send Email section so the link actually reaches the customer."
  },
  {
    name: "shopify_apply_coupon",
    write: true,
    label: "Coupon code validation",
    group: "Shopify — Promotions",
    description: "Validates a discount code in real time."
  },
  {
    name: "shopify_check_stock",
    label: "Live stock levels",
    group: "Shopify — Products",
    description: "Checks real-time inventory."
  },
  {
    name: "shopify_create_quote",
    write: true,
    label: "Quote builder / checkout link",
    group: "Shopify — Orders",
    description: "Creates a draft order and sends a direct checkout link.",
    channelWarning:
      "Generates a clickable checkout URL. On IVR, add a Send SMS section to deliver the link to the customer's phone."
  },
  {
    name: "shopify_initiate_return",
    write: true,
    label: "Self-service returns",
    group: "Shopify — Orders",
    description: "Submits a return request directly in Shopify."
  },
  {
    name: "shopify_get_tracking",
    label: "Order tracking",
    group: "Shopify — Orders",
    description: "Fetches live tracking info and carrier link for any order."
  },
  {
    name: "shopify_get_reviews",
    label: "Product reviews (Judge.me)",
    group: "Shopify — Products",
    description: "Fetches product reviews from Judge.me."
  },
  {
    name: "shopify_get_cart",
    label: "View active cart",
    group: "Shopify — Orders",
    description:
      "Retrieves cart contents, item count, subtotal, and applied discounts."
  },
  {
    name: "shopify_get_product_details",
    label: "Full product details",
    group: "Shopify — Products",
    description:
      "Full product details by ID — variants, images, pricing, inventory."
  },
  {
    name: "shopify_get_shop_info",
    label: "Store info",
    group: "Shopify — Products",
    description: "Returns store name, URL, currency, and contact information."
  },
  {
    name: "shopify_cancel_order",
    label: "Cancel order",
    group: "Shopify — Orders",
    description: "Cancels a Shopify order and triggers the cancellation flow.",
    destructive: true
  },
  {
    name: "shopify_get_refunds",
    label: "Get refunds",
    group: "Shopify — Orders",
    description: "Returns all refunds on a given order."
  },
  {
    name: "shopify_create_refund",
    label: "Create refund",
    group: "Shopify — Orders",
    description: "Issues a partial or full refund on a Shopify order.",
    destructive: true
  },
  {
    name: "shopify_get_collections",
    label: "Product collections",
    group: "Shopify — Products",
    description: "Returns product collections and their contents."
  },
  {
    name: "shopify_get_product_variants",
    label: "Product variants",
    group: "Shopify — Products",
    description: "Returns all variants for a product."
  },
  {
    name: "shopify_add_order_note",
    write: true,
    label: "Add order note",
    group: "Shopify — Orders",
    description: "Adds an internal note to a Shopify order."
  },
  {
    name: "shopify_get_order_notes",
    label: "Order notes",
    group: "Shopify — Orders",
    description: "Retrieves internal notes on a Shopify order."
  },
  {
    name: "shopify_get_shipping_zones",
    label: "Shipping zones",
    group: "Shopify — Products",
    description: "Returns available shipping zones and rates."
  },
  {
    name: "shopify_get_discount_codes",
    label: "Discount codes",
    group: "Shopify — Promotions",
    description: "Lists active discount codes for the store."
  },
  {
    name: "shopify_compare_products",
    label: "Compare products",
    group: "Shopify — Products",
    description: "Compares multiple products side by side."
  },
  {
    name: "shopify_recover_cart",
    label: "Recover abandoned cart",
    group: "Shopify — Orders",
    description: "Sends a cart recovery link to a visitor by email.",
    channelWarning:
      "Sends an email with a recovery link. Requires a valid email address — on IVR, add an Ask Question node to collect the customer's email before this skill runs."
  },
  {
    name: "shopify_get_customer_insights",
    label: "Customer intelligence",
    group: "Shopify — Customers",
    description: "Returns LTV, order frequency, and buying patterns."
  },
  {
    name: "shopify_get_recommendations",
    label: "Product recommendations",
    group: "Shopify — Products",
    description: "Returns AI-powered product recommendations for the visitor."
  },
  {
    name: "shopify_create_discount",
    write: true,
    label: "Create discount code",
    group: "Shopify — Promotions",
    description: "Creates a new discount code for the visitor."
  },
  {
    name: "shopify_get_store_policies",
    label: "Store policies",
    group: "Shopify — Products",
    description: "Returns refund, privacy, and shipping policies."
  },
  {
    name: "shopify_get_metafields",
    label: "Get custom metafields",
    group: "Shopify — Products",
    description: "Returns custom metafield values for a product or order."
  },
  {
    name: "shopify_get_blog_articles",
    label: "Search blog articles",
    group: "Shopify — Products",
    description: "Searches Shopify blog content by keyword."
  },
  {
    name: "shopify_create_fulfillment",
    label: "Mark order shipped",
    group: "Shopify — Orders",
    description: "Creates a fulfillment for a Shopify order.",
    destructive: true
  },
  {
    name: "shopify_get_draft_orders",
    label: "Get draft orders",
    group: "Shopify — Orders",
    description: "Returns open draft orders for a customer."
  },
  {
    name: "shopify_update_order_tags",
    write: true,
    label: "Tag order",
    group: "Shopify — Orders",
    description: "Adds or replaces tags on a Shopify order."
  },

  // ── BigCommerce ────────────────────────────────────────────────────────────
  {
    name: "bigcommerce_get_customer",
    label: "Get customer details",
    group: "BigCommerce — Customers",
    description:
      "Fetches full customer record including addresses and payment methods."
  },
  {
    name: "bigcommerce_get_order",
    label: "Get order by ID",
    group: "BigCommerce — Orders",
    description:
      "Retrieves a specific order with status, line items, and tracking."
  },
  {
    name: "bigcommerce_search_orders",
    label: "Search orders by email",
    group: "BigCommerce — Orders",
    description: "Looks up recent orders for a customer email."
  },
  {
    name: "bigcommerce_search_products",
    label: "Search products",
    group: "BigCommerce — Products",
    description: "Searches the product catalog by keyword."
  },
  {
    name: "bigcommerce_create_cart",
    write: true,
    label: "Create cart / quote link",
    group: "BigCommerce — Orders",
    description: "Builds a cart and returns a direct checkout link.",
    channelWarning:
      "Returns a clickable checkout URL. On IVR, add a Send SMS section to deliver the link to the customer's phone."
  },
  {
    name: "bigcommerce_get_abandoned_carts",
    label: "Find abandoned carts",
    group: "BigCommerce — Orders",
    description: "Finds carts abandoned mid-checkout by customer email.",
    channelWarning:
      "Returns a checkout URL. On IVR/SMS, add a Send SMS or Send Email section so the recovery link actually reaches the customer."
  },
  {
    name: "bigcommerce_apply_coupon",
    write: true,
    label: "Apply coupon code",
    group: "BigCommerce — Promotions",
    description: "Applies a discount coupon to the visitor's cart."
  },
  {
    name: "bigcommerce_get_cart",
    label: "View active cart",
    group: "BigCommerce — Orders",
    description: "Gets the active cart for a customer by email."
  },
  {
    name: "bigcommerce_add_order_note",
    write: true,
    label: "Add order note",
    group: "BigCommerce — Orders",
    description: "Adds an internal staff note to a BigCommerce order."
  },
  {
    name: "bigcommerce_check_stock",
    label: "Check stock level",
    group: "BigCommerce — Products",
    description: "Checks real-time inventory for a product."
  },
  {
    name: "bigcommerce_get_tracking",
    label: "Get shipment tracking",
    group: "BigCommerce — Orders",
    description: "Returns carrier, tracking number, and tracking URL."
  },
  {
    name: "bigcommerce_get_reviews",
    label: "Get product reviews",
    group: "BigCommerce — Products",
    description: "Fetches approved customer reviews from BigCommerce."
  },
  {
    name: "bigcommerce_get_variants",
    label: "Get product variants",
    group: "BigCommerce — Products",
    description: "Returns all variants for a product."
  },
  {
    name: "bigcommerce_check_pricing",
    label: "Check customer pricing",
    group: "BigCommerce — Products",
    description: "Looks up customer-group-specific pricing for a product."
  },
  {
    name: "bigcommerce_get_brand_products",
    label: "Browse by brand",
    group: "BigCommerce — Products",
    description: "Returns products filtered by brand name."
  },
  {
    name: "bigcommerce_cancel_order",
    label: "Cancel order",
    group: "BigCommerce — Orders",
    description: "Cancels a BigCommerce order.",
    destructive: true
  },
  {
    name: "bigcommerce_get_refunds",
    label: "Get refunds",
    group: "BigCommerce — Orders",
    description: "Returns all refunds on a given order."
  },
  {
    name: "bigcommerce_get_invoices",
    label: "Get invoices",
    group: "BigCommerce — Orders",
    description: "Returns invoice records for a customer."
  },
  {
    name: "bigcommerce_update_cart",
    write: true,
    label: "Update cart",
    group: "BigCommerce — Orders",
    description: "Adds or removes items from a customer's active cart."
  },
  {
    name: "bigcommerce_get_wishlist",
    label: "Get wishlist",
    group: "BigCommerce — Customers",
    description: "Returns the customer's saved wishlist items."
  },
  {
    name: "bigcommerce_get_shipping_methods",
    label: "Shipping methods",
    group: "BigCommerce — Products",
    description: "Returns available shipping methods and rates."
  },
  {
    name: "bigcommerce_get_store_config",
    label: "Store config",
    group: "BigCommerce — Products",
    description: "Returns store configuration and settings."
  },
  {
    name: "bigcommerce_get_categories",
    label: "Browse categories",
    group: "BigCommerce — Products",
    description: "Returns product categories for catalog navigation."
  },
  {
    name: "bigcommerce_compare_products",
    label: "Compare products",
    group: "BigCommerce — Products",
    description: "Compares multiple products side by side."
  },
  {
    name: "bigcommerce_customer_insights",
    label: "Customer intelligence",
    group: "BigCommerce — Customers",
    description: "Returns LTV, order frequency, and buying patterns."
  },
  {
    name: "bigcommerce_get_recommendations",
    label: "Product recommendations",
    group: "BigCommerce — Products",
    description: "Returns personalized product recommendations."
  },
  {
    name: "bigcommerce_create_discount",
    write: true,
    label: "Create discount code",
    group: "BigCommerce — Promotions",
    description: "Creates a new coupon code for the visitor."
  },
  {
    name: "bigcommerce_create_refund",
    label: "Create refund",
    group: "BigCommerce — Orders",
    description: "Issues a refund on a BigCommerce order.",
    destructive: true
  },
  {
    name: "bigcommerce_get_custom_fields",
    label: "Get product custom fields",
    group: "BigCommerce — Products",
    description: "Returns custom field values for a product."
  },
  {
    name: "bigcommerce_get_gift_certificates",
    label: "Look up gift certificate",
    group: "BigCommerce — Promotions",
    description: "Looks up a gift certificate by code."
  },
  {
    name: "bigcommerce_get_orders",
    label: "Get customer orders",
    group: "BigCommerce — Orders",
    description: "Returns order history for a customer."
  },
  {
    name: "bigcommerce_get_price_lists",
    label: "Get price lists",
    group: "BigCommerce — Products",
    description: "Returns B2B price lists applicable to the customer."
  },
  {
    name: "bigcommerce_create_quote",
    write: true,
    label: "Create B2B quote",
    group: "BigCommerce — Orders",
    description: "Creates a formal B2B quote."
  },

  // ── WooCommerce ────────────────────────────────────────────────────────────
  // Backfilled 2026-07-16 — these skills existed server-side with zero entries here,
  // invisible to the skill picker even though EnableWooCommerce customers had them.
  {
    name: "woocommerce_search_products",
    label: "Search products",
    group: "WooCommerce — Products",
    description: "Searches the product catalog by keyword."
  },
  {
    name: "woocommerce_get_product_by_sku",
    label: "Get product by SKU",
    group: "WooCommerce — Products",
    description: "Fetches a specific product by SKU."
  },
  {
    name: "woocommerce_check_stock",
    label: "Check stock level",
    group: "WooCommerce — Products",
    description: "Checks real-time inventory for a product."
  },
  {
    name: "woocommerce_get_product_reviews",
    label: "Get product reviews",
    group: "WooCommerce — Products",
    description: "Fetches approved customer reviews."
  },
  {
    name: "woocommerce_get_product_variations",
    label: "Get product variations",
    group: "WooCommerce — Products",
    description: "Returns all variations for a product."
  },
  {
    name: "woocommerce_get_categories",
    label: "Browse categories",
    group: "WooCommerce — Products",
    description: "Returns product categories for catalog navigation."
  },
  {
    name: "woocommerce_get_top_sellers",
    label: "Top sellers",
    group: "WooCommerce — Products",
    description: "Returns best-selling products."
  },
  {
    name: "woocommerce_get_recommendations",
    label: "Product recommendations",
    group: "WooCommerce — Products",
    description: "Returns personalized product recommendations."
  },
  {
    name: "woocommerce_compare_products",
    label: "Compare products",
    group: "WooCommerce — Products",
    description: "Compares multiple products side by side."
  },
  {
    name: "woocommerce_get_shipping_methods",
    label: "Shipping methods",
    group: "WooCommerce — Products",
    description: "Returns available shipping methods and rates."
  },
  {
    name: "woocommerce_get_tax_rates",
    label: "Tax rates",
    group: "WooCommerce — Products",
    description: "Returns configured tax rates."
  },
  {
    name: "woocommerce_get_store_settings",
    label: "Store settings",
    group: "WooCommerce — Products",
    description: "Returns store configuration and settings."
  },
  {
    name: "woocommerce_get_customer",
    label: "Get customer details",
    group: "WooCommerce — Customers",
    description: "Fetches full customer record."
  },
  {
    name: "woocommerce_get_customer_orders",
    label: "Get customer orders",
    group: "WooCommerce — Customers",
    description: "Returns order history for a customer."
  },
  {
    name: "woocommerce_customer_insights",
    label: "Customer intelligence",
    group: "WooCommerce — Customers",
    description: "Returns LTV, order frequency, and buying patterns."
  },
  {
    name: "woocommerce_get_order",
    label: "Get order by ID",
    group: "WooCommerce — Orders",
    description:
      "Retrieves a specific order with status, line items, and tracking."
  },
  {
    name: "woocommerce_search_orders",
    label: "Search orders",
    group: "WooCommerce — Orders",
    description: "Looks up recent orders for a customer."
  },
  {
    name: "woocommerce_build_cart_link",
    label: "Build cart / checkout link",
    group: "WooCommerce — Orders",
    description: "Builds a cart and returns a direct checkout link.",
    channelWarning:
      "Returns a clickable checkout URL. On IVR, add a Send SMS section to deliver the link to the customer's phone."
  },
  {
    name: "woocommerce_validate_coupon",
    label: "Coupon code validation",
    group: "WooCommerce — Promotions",
    description: "Validates a discount code in real time."
  },
  {
    name: "woocommerce_create_coupon",
    write: true,
    label: "Create coupon code",
    group: "WooCommerce — Promotions",
    description: "Creates a new coupon code for the visitor."
  },
  {
    name: "woocommerce_get_payment_gateways",
    label: "Payment gateways",
    group: "WooCommerce — Orders",
    description: "Returns available payment gateways."
  },
  {
    name: "woocommerce_get_sales_report",
    label: "Sales report",
    group: "WooCommerce — Orders",
    description: "Returns store sales report data."
  },
  {
    name: "woocommerce_create_order_note",
    write: true,
    label: "Add order note",
    group: "WooCommerce — Orders",
    description: "Adds an internal staff note to an order."
  },
  {
    name: "woocommerce_get_order_notes",
    label: "Get order notes",
    group: "WooCommerce — Orders",
    description: "Retrieves internal notes on an order."
  },
  {
    name: "woocommerce_get_refunds",
    label: "Get refunds",
    group: "WooCommerce — Orders",
    description: "Returns all refunds on a given order."
  },
  {
    name: "woocommerce_create_refund",
    label: "Create refund",
    group: "WooCommerce — Orders",
    description: "Issues a partial or full refund.",
    destructive: true
  },
  {
    name: "woocommerce_cancel_order",
    label: "Cancel order",
    group: "WooCommerce — Orders",
    description: "Cancels a WooCommerce order.",
    destructive: true
  },
  {
    name: "woocommerce_update_order_status",
    label: "Update order status",
    group: "WooCommerce — Orders",
    description: "Updates the status of an order.",
    destructive: true
  },

  // Squarespace (read-only, no cart/checkout API)
  {
    name: "squarespace_get_order",
    label: "Get order",
    group: "Squarespace — Orders",
    description: "Looks up a specific order by ID: status, total, tracking."
  },
  {
    name: "squarespace_search_orders",
    label: "Search orders",
    group: "Squarespace — Orders",
    description: "Finds orders by customer email or fulfillment status."
  },
  {
    name: "squarespace_get_customer_orders",
    label: "Get customer order history",
    group: "Squarespace — Orders",
    description: "Lists a customer's full order history by email."
  },
  {
    name: "squarespace_search_products",
    label: "Search products",
    group: "Squarespace — Products",
    description: "Searches the product catalog by name or SKU."
  },
  {
    name: "squarespace_get_product",
    label: "Get product",
    group: "Squarespace — Products",
    description: "Retrieves full details for a single product by ID."
  },
  {
    name: "squarespace_check_inventory",
    label: "Check inventory",
    group: "Squarespace — Products",
    description: "Checks live stock quantity for a product or SKU."
  },
  {
    name: "squarespace_get_store_info",
    label: "Get store info",
    group: "Squarespace — Store",
    description: "Confirms the connected store URL and connection status."
  },

  // ── Magento ────────────────────────────────────────────────────────────────
  // Backfilled 2026-07-16 — same gap as WooCommerce above.
  {
    name: "magento_search_products",
    label: "Search products",
    group: "Magento — Products",
    description: "Searches the product catalog by keyword."
  },
  {
    name: "magento_check_stock",
    label: "Check stock level",
    group: "Magento — Products",
    description: "Checks real-time inventory for a product."
  },
  {
    name: "magento_get_reviews",
    label: "Get product reviews",
    group: "Magento — Products",
    description: "Fetches customer reviews."
  },
  {
    name: "magento_get_configurable_options",
    label: "Get configurable options",
    group: "Magento — Products",
    description: "Returns configurable product options."
  },
  {
    name: "magento_get_categories",
    label: "Browse categories",
    group: "Magento — Products",
    description: "Returns product categories for catalog navigation."
  },
  {
    name: "magento_get_tier_pricing",
    label: "Tier pricing",
    group: "Magento — Products",
    description: "Returns quantity-based tier pricing for a product."
  },
  {
    name: "magento_get_recommendations",
    label: "Product recommendations",
    group: "Magento — Products",
    description: "Returns personalized product recommendations."
  },
  {
    name: "magento_get_shipping_methods",
    label: "Shipping methods",
    group: "Magento — Products",
    description: "Returns available shipping methods and rates."
  },
  {
    name: "magento_get_store_config",
    label: "Store config",
    group: "Magento — Products",
    description: "Returns store configuration and settings."
  },
  {
    name: "magento_search_customers",
    label: "Search customers",
    group: "Magento — Customers",
    description: "Searches customer records by name or email."
  },
  {
    name: "magento_get_company",
    label: "Get company (B2B)",
    group: "Magento — Customers",
    description: "Returns B2B company account details."
  },
  {
    name: "magento_customer_insights",
    label: "Customer intelligence",
    group: "Magento — Customers",
    description: "Returns LTV, order frequency, and buying patterns."
  },
  {
    name: "magento_get_wishlist",
    label: "Get wishlist",
    group: "Magento — Customers",
    description: "Returns the customer's saved wishlist items."
  },
  {
    name: "magento_get_order",
    label: "Get order by ID",
    group: "Magento — Orders",
    description: "Retrieves a specific order with status and line items."
  },
  {
    name: "magento_search_orders",
    label: "Search orders",
    group: "Magento — Orders",
    description: "Looks up recent orders for a customer."
  },
  {
    name: "magento_get_tracking",
    label: "Get shipment tracking",
    group: "Magento — Orders",
    description: "Returns carrier, tracking number, and tracking URL."
  },
  {
    name: "magento_get_cart",
    label: "View active cart",
    group: "Magento — Orders",
    description: "Retrieves cart contents for a customer."
  },
  {
    name: "magento_create_cart",
    write: true,
    label: "Create cart / quote link",
    group: "Magento — Orders",
    description: "Builds a cart and returns a direct checkout link.",
    channelWarning:
      "Returns a clickable checkout URL. On IVR, add a Send SMS section to deliver the link to the customer's phone."
  },
  {
    name: "magento_update_cart",
    write: true,
    label: "Update cart",
    group: "Magento — Orders",
    description: "Adds or removes items from a customer's active cart."
  },
  {
    name: "magento_apply_coupon",
    write: true,
    label: "Apply coupon code",
    group: "Magento — Promotions",
    description: "Applies a discount coupon to the visitor's cart."
  },
  {
    name: "magento_create_coupon",
    write: true,
    label: "Create coupon code",
    group: "Magento — Promotions",
    description: "Creates a new coupon code for the visitor."
  },
  {
    name: "magento_get_negotiable_quotes",
    label: "Negotiable quotes (B2B)",
    group: "Magento — Orders",
    description: "Returns B2B negotiable quotes for a company account."
  },
  {
    name: "magento_get_requisition_lists",
    label: "Requisition lists (B2B)",
    group: "Magento — Orders",
    description: "Returns B2B requisition lists for a company account."
  },
  {
    name: "magento_get_invoice",
    label: "Get invoices",
    group: "Magento — Orders",
    description: "Returns invoice records for an order."
  },
  {
    name: "magento_get_credit_memos",
    label: "Get credit memos",
    group: "Magento — Orders",
    description: "Returns credit memo (refund) records for an order."
  },
  {
    name: "magento_get_refunds",
    label: "Get refunds",
    group: "Magento — Orders",
    description: "Returns all refunds on a given order."
  },
  {
    name: "magento_create_refund",
    label: "Create refund",
    group: "Magento — Orders",
    description: "Issues a partial or full refund.",
    destructive: true
  },
  {
    name: "magento_cancel_order",
    label: "Cancel order",
    group: "Magento — Orders",
    description: "Cancels a Magento order.",
    destructive: true
  },
  {
    name: "magento_add_order_comment",
    write: true,
    label: "Add order comment",
    group: "Magento — Orders",
    description: "Adds an internal staff comment to an order."
  },

  // ── HubSpot ────────────────────────────────────────────────────────────────
  {
    name: "hubspot_search_contact",
    label: "Search contact",
    group: "HubSpot — Contacts",
    description: "Searches HubSpot contacts by name or email."
  },
  {
    name: "hubspot_get_contact",
    label: "Get contact details",
    group: "HubSpot — Contacts",
    description: "Returns full contact record from HubSpot."
  },
  {
    name: "hubspot_update_contact",
    write: true,
    label: "Update contact",
    group: "HubSpot — Contacts",
    description: "Updates contact properties in HubSpot."
  },
  {
    name: "hubspot_create_contact",
    write: true,
    label: "Create contact",
    group: "HubSpot — Contacts",
    description: "Creates a new contact in HubSpot."
  },
  {
    name: "hubspot_search_contacts",
    label: "Advanced contact search",
    group: "HubSpot — Contacts",
    description: "Advanced search across HubSpot contacts."
  },
  {
    name: "hubspot_search_companies",
    label: "Search companies",
    group: "HubSpot — Companies",
    description: "Searches HubSpot companies by name or domain."
  },
  {
    name: "hubspot_get_company",
    label: "Get company details",
    group: "HubSpot — Companies",
    description: "Returns full company record from HubSpot."
  },
  {
    name: "hubspot_get_contact_companies",
    label: "Get contact's companies",
    group: "HubSpot — Companies",
    description: "Returns companies associated with a contact."
  },
  {
    name: "hubspot_get_deals",
    label: "Get contact's deals",
    group: "HubSpot — Deals",
    description: "Returns deals associated with a contact."
  },
  {
    name: "hubspot_search_deals",
    label: "Search deals",
    group: "HubSpot — Deals",
    description: "Searches HubSpot deals by name or stage."
  },
  {
    name: "hubspot_get_deal",
    label: "Get deal details",
    group: "HubSpot — Deals",
    description: "Returns full deal record from HubSpot."
  },
  {
    name: "hubspot_update_deal",
    write: true,
    label: "Update deal",
    group: "HubSpot — Deals",
    description: "Updates deal properties in HubSpot."
  },
  {
    name: "hubspot_create_deal",
    write: true,
    label: "Create deal",
    group: "HubSpot — Deals",
    description: "Creates a new deal in HubSpot."
  },
  {
    name: "hubspot_get_pipeline_stages",
    label: "Get pipeline stages",
    group: "HubSpot — Deals",
    description: "Returns pipeline stages for deal routing."
  },
  {
    name: "hubspot_create_quote",
    write: true,
    label: "Create quote",
    group: "HubSpot — Deals",
    description: "Creates a HubSpot quote for a deal."
  },
  {
    name: "hubspot_create_ticket",
    write: true,
    label: "Create ticket",
    group: "HubSpot — Tickets",
    description: "Creates a new support ticket in HubSpot."
  },
  {
    name: "hubspot_get_ticket",
    label: "Get ticket details",
    group: "HubSpot — Tickets",
    description: "Returns full ticket record from HubSpot."
  },
  {
    name: "hubspot_get_contact_tickets",
    label: "Get contact's tickets",
    group: "HubSpot — Tickets",
    description: "Returns tickets associated with a contact."
  },
  {
    name: "hubspot_search_tickets",
    label: "Search tickets",
    group: "HubSpot — Tickets",
    description: "Searches HubSpot tickets by subject or status."
  },
  {
    name: "hubspot_update_ticket",
    write: true,
    label: "Update ticket",
    group: "HubSpot — Tickets",
    description: "Updates ticket properties in HubSpot."
  },
  {
    name: "hubspot_get_contact_tasks",
    label: "Get contact's tasks",
    group: "HubSpot — Tasks",
    description: "Returns tasks associated with a contact."
  },
  {
    name: "hubspot_create_task",
    write: true,
    label: "Create task",
    group: "HubSpot — Tasks",
    description: "Creates a new task in HubSpot."
  },
  {
    name: "hubspot_add_note",
    write: true,
    label: "Add note to contact",
    group: "HubSpot — Engagement",
    description: "Logs a note on a contact record."
  },
  {
    name: "hubspot_get_contact_timeline",
    label: "Get contact timeline",
    group: "HubSpot — Engagement",
    description: "Returns activity timeline for a contact."
  },
  {
    name: "hubspot_get_owners",
    label: "Get HubSpot owners",
    group: "HubSpot — Engagement",
    description: "Returns HubSpot users available for assignment."
  },
  {
    name: "hubspot_create_engagement",
    write: true,
    label: "Log call or meeting",
    group: "HubSpot — Engagement",
    description: "Logs a call or meeting on a contact."
  },
  {
    name: "hubspot_enroll_sequence",
    label: "Enroll in sequence",
    group: "HubSpot — Engagement",
    description: "Enrolls a contact in a HubSpot sequence."
  },
  {
    name: "hubspot_get_form_submissions",
    label: "Get form submissions",
    group: "HubSpot — Engagement",
    description: "Returns form submission history for a contact."
  },
  {
    name: "hubspot_get_meetings",
    label: "Get meetings",
    group: "HubSpot — Engagement",
    description: "Returns scheduled meetings for a contact."
  },

  // ── Dynamics 365 ──────────────────────────────────────────────────────────
  {
    name: "dynamics365_search_contacts",
    label: "Search contacts",
    group: "Dynamics — Contacts",
    description: "Searches Dynamics 365 contacts."
  },
  {
    name: "dynamics365_get_contact",
    label: "Get contact details",
    group: "Dynamics — Contacts",
    description: "Returns full contact record."
  },
  {
    name: "dynamics365_get_contact_by_email",
    label: "Get contact by email",
    group: "Dynamics — Contacts",
    description: "Looks up a contact by email address."
  },
  {
    name: "dynamics365_update_contact",
    write: true,
    label: "Update contact",
    group: "Dynamics — Contacts",
    description: "Updates contact fields in Dynamics."
  },
  {
    name: "dynamics365_search_accounts",
    label: "Search accounts",
    group: "Dynamics — Accounts",
    description: "Searches Dynamics 365 accounts."
  },
  {
    name: "dynamics365_get_account",
    label: "Get account details",
    group: "Dynamics — Accounts",
    description: "Returns full account record."
  },
  {
    name: "dynamics365_get_account_contacts",
    label: "Get account contacts",
    group: "Dynamics — Accounts",
    description: "Returns contacts associated with an account."
  },
  {
    name: "dynamics365_create_lead",
    write: true,
    label: "Create lead",
    group: "Dynamics — Leads",
    description: "Creates a new lead in Dynamics."
  },
  {
    name: "dynamics365_search_leads",
    label: "Search leads",
    group: "Dynamics — Leads",
    description: "Searches Dynamics leads."
  },
  {
    name: "dynamics365_update_lead",
    write: true,
    label: "Update lead",
    group: "Dynamics — Leads",
    description: "Updates lead fields in Dynamics."
  },
  {
    name: "dynamics365_search_opportunities",
    label: "Search opportunities",
    group: "Dynamics — Opportunities",
    description: "Searches Dynamics opportunities."
  },
  {
    name: "dynamics365_get_opportunity",
    label: "Get opportunity details",
    group: "Dynamics — Opportunities",
    description: "Returns full opportunity record."
  },
  {
    name: "dynamics365_get_contact_opportunities",
    label: "Get contact's opportunities",
    group: "Dynamics — Opportunities",
    description: "Returns opportunities for a contact."
  },
  {
    name: "dynamics365_update_opportunity",
    write: true,
    label: "Update opportunity",
    group: "Dynamics — Opportunities",
    description: "Updates opportunity fields in Dynamics."
  },
  {
    name: "dynamics365_create_opportunity",
    write: true,
    label: "Create opportunity",
    group: "Dynamics — Opportunities",
    description: "Creates a new opportunity."
  },
  {
    name: "dynamics365_get_products",
    label: "Search products",
    group: "Dynamics — Opportunities",
    description:
      "Searches the Dynamics 365 product catalog by name or product number."
  },
  {
    name: "dynamics365_create_quote",
    write: true,
    label: "Create quote",
    group: "Dynamics — Opportunities",
    description: "Creates a Dynamics quote for an opportunity."
  },
  {
    name: "dynamics365_close_opportunity",
    write: true,
    label: "Close opportunity",
    group: "Dynamics — Opportunities",
    description: "Closes a Dynamics opportunity as Won or Lost."
  },
  {
    name: "dynamics365_search_cases",
    label: "Search cases",
    group: "Dynamics — Cases",
    description: "Searches Dynamics support cases."
  },
  {
    name: "dynamics365_get_case",
    label: "Get case details",
    group: "Dynamics — Cases",
    description: "Returns full case record."
  },
  {
    name: "dynamics365_create_case",
    write: true,
    label: "Create case",
    group: "Dynamics — Cases",
    description: "Creates a new support case."
  },
  {
    name: "dynamics365_update_case",
    write: true,
    label: "Update case",
    group: "Dynamics — Cases",
    description: "Updates case fields in Dynamics."
  },
  {
    name: "dynamics365_close_case",
    write: true,
    label: "Close case",
    group: "Dynamics — Cases",
    description: "Resolves and closes a Dynamics support case."
  },
  {
    name: "dynamics365_get_contact_cases",
    label: "Get contact's cases",
    group: "Dynamics — Cases",
    description: "Returns cases for a contact."
  },
  {
    name: "dynamics365_create_note",
    write: true,
    label: "Create note",
    group: "Dynamics — Activity",
    description: "Creates a note on a Dynamics record."
  },
  {
    name: "dynamics365_get_activities",
    label: "Get activities",
    group: "Dynamics — Activity",
    description: "Returns activities for a contact or account."
  },
  {
    name: "dynamics365_create_activity",
    write: true,
    label: "Create activity",
    group: "Dynamics — Activity",
    description: "Creates a phone call or task activity."
  },
  {
    name: "dynamics365_get_entity_notes",
    label: "Get entity notes",
    group: "Dynamics — Activity",
    description: "Returns all notes on a record."
  },
  {
    name: "dynamics365_schedule_appointment",
    write: true,
    label: "Schedule appointment",
    group: "Dynamics — Activity",
    description: "Schedules an appointment in Dynamics."
  },
  {
    name: "dynamics365_get_email_history",
    label: "Get email history",
    group: "Dynamics — Activity",
    description: "Returns email activity history for a contact."
  },
  {
    name: "dynamics365_create_rma",
    write: true,
    label: "Create RMA",
    group: "Dynamics — Field Service",
    description:
      "Creates a Return Merchandise Authorization in Dynamics 365 Field Service."
  },
  {
    name: "dynamics365_get_rma_status",
    label: "Get RMA status",
    group: "Dynamics — Field Service",
    description:
      "Looks up the status of a Dynamics 365 Field Service RMA by number or ID."
  },
  {
    name: "dynamics365_get_field_service_order",
    label: "Get field service work order",
    group: "Dynamics — Field Service",
    description:
      "Retrieves a Dynamics 365 Field Service work order by ID or work order number."
  },

  // ── NetSuite ───────────────────────────────────────────────────────────────
  {
    name: "netsuite_search_customer",
    label: "Search customers",
    group: "NetSuite — Customers",
    description: "Searches NetSuite customer records."
  },
  {
    name: "netsuite_get_customer",
    label: "Full customer profile",
    group: "NetSuite — Customers",
    description: "Returns full customer record from NetSuite."
  },
  {
    name: "netsuite_get_customer_contacts",
    label: "Customer contacts",
    group: "NetSuite — Customers",
    description: "Returns contacts associated with a customer."
  },
  {
    name: "netsuite_get_customer_balance",
    label: "Customer balance",
    group: "NetSuite — Customers",
    description: "Returns AR balance and credit limit."
  },
  {
    name: "netsuite_update_customer",
    write: true,
    label: "Update customer",
    group: "NetSuite — Customers",
    description: "Updates customer record fields in NetSuite."
  },
  {
    name: "netsuite_create_lead",
    write: true,
    label: "Create lead / contact",
    group: "NetSuite — Leads",
    description:
      "Records a new prospect from the conversation. Whether it lands as a Lead or as a full Customer record is controlled by the Contact Creation setting on your NetSuite config (overridable per workflow node)."
  },
  {
    name: "netsuite_search_leads",
    label: "Search leads",
    group: "NetSuite — Leads",
    description: "Searches NetSuite lead records."
  },
  {
    name: "netsuite_update_lead",
    write: true,
    label: "Update lead",
    group: "NetSuite — Leads",
    description: "Updates fields on an existing NetSuite lead."
  },
  {
    name: "netsuite_search_orders",
    label: "Search orders",
    group: "NetSuite — Orders",
    description: "Searches NetSuite sales orders."
  },
  {
    name: "netsuite_get_order",
    label: "Order details",
    group: "NetSuite — Orders",
    description: "Returns full order record from NetSuite."
  },
  {
    name: "netsuite_get_order_history",
    label: "Order history",
    group: "NetSuite — Orders",
    description: "Returns order history for a customer."
  },
  {
    name: "netsuite_cancel_order",
    label: "Cancel / hold order",
    group: "NetSuite — Orders",
    description: "Cancels or places a hold on an order.",
    destructive: true
  },
  {
    name: "netsuite_add_order_note",
    write: true,
    label: "Add order note",
    group: "NetSuite — Orders",
    description: "Adds an internal memo to a sales order."
  },
  {
    name: "netsuite_get_invoice",
    label: "Invoice details",
    group: "NetSuite — Invoicing",
    description: "Returns invoice details from NetSuite."
  },
  {
    name: "netsuite_get_open_invoices",
    label: "Open invoices",
    group: "NetSuite — Invoicing",
    description: "Returns all open invoices for a customer."
  },
  {
    name: "netsuite_get_payment_history",
    label: "Payment history",
    group: "NetSuite — Invoicing",
    description: "Returns payment history for a customer."
  },
  {
    name: "netsuite_get_credit_memos",
    label: "Credit memos",
    group: "NetSuite — Invoicing",
    description: "Returns credit memos for a customer."
  },
  {
    name: "netsuite_create_case",
    write: true,
    label: "Create support case",
    group: "NetSuite — Cases",
    description: "Creates a support case in NetSuite."
  },
  {
    name: "netsuite_get_cases",
    label: "Get cases",
    group: "NetSuite — Cases",
    description: "Returns support cases for a customer."
  },
  {
    name: "netsuite_update_case",
    write: true,
    label: "Update case",
    group: "NetSuite — Cases",
    description: "Updates a support case in NetSuite."
  },
  {
    name: "netsuite_add_case_note",
    write: true,
    label: "Add case note",
    group: "NetSuite — Cases",
    description: "Adds a note to a support case."
  },
  {
    name: "netsuite_get_rma",
    label: "RMA status",
    group: "NetSuite — Returns",
    description: "Returns RMA record from NetSuite."
  },
  {
    name: "netsuite_create_rma",
    write: true,
    label: "Create RMA",
    group: "NetSuite — Returns",
    description: "Creates a return authorization in NetSuite."
  },
  {
    name: "netsuite_cti_setup_info",
    label: "Click-to-dial setup info",
    group: "NetSuite — Telephony",
    description:
      "Reports whether click-to-dial (CTI) is set up and returns the paste-into-NetSuite URL, or instructions to generate one."
  },
  {
    name: "netsuite_search_items",
    label: "Search products",
    group: "NetSuite — Items",
    description: "Search products by keyword or SKU."
  },
  {
    name: "netsuite_check_stock",
    label: "Check stock",
    group: "NetSuite — Items",
    description: "Inventory levels across locations."
  },
  {
    name: "netsuite_get_item_pricing",
    label: "Item pricing",
    group: "NetSuite — Items",
    description: "Base price, cost, online price."
  },
  {
    name: "netsuite_get_opportunity",
    label: "Get opportunity",
    group: "NetSuite — Opportunities",
    description:
      "Get a specific NetSuite opportunity record by ID. Returns stage, probability, expected amount, and close date — lets the AI provide deal status updates during sales conversations."
  },
  {
    name: "netsuite_search_opportunities",
    label: "Search opportunities",
    group: "NetSuite — Opportunities",
    description:
      "Search NetSuite opportunities by customer, stage, or date range. Returns a list of matching deals with status, amount, and owner."
  },
  {
    name: "netsuite_get_subsidiaries",
    label: "Get subsidiaries",
    group: "NetSuite — Customers",
    description:
      "List all active subsidiaries (legal entities and locations) in NetSuite. Essential for multi-entity B2B environments where customers belong to a specific subsidiary."
  },
  {
    name: "netsuite_create_quote",
    label: "Create quote",
    group: "NetSuite — Invoicing",
    description:
      "Create a NetSuite estimate (quote) record for a customer with line items, pricing, and expiry. Returns the new quote number — close B2B sales directly from chat."
  },
  {
    name: "netsuite_get_credit_memo",
    label: "Get credit memo",
    group: "NetSuite — Invoicing",
    description:
      "Get details on a specific NetSuite credit memo by ID — amount, applied invoices, and status. Use to answer 'where is my credit?' during support conversations."
  },
  {
    name: "netsuite_restlet",
    label: "Call Custom RESTlet",
    group: "NetSuite — Advanced",
    description:
      "Execute a pre-deployed RESTlet in your NetSuite account for custom business logic (warranty checks, contract pricing, multi-step workflows). Requires RESTlet URL in integration settings."
  },

  // ── Monday.com ────────────────────────────────────────────────────────────
  {
    name: "monday_push_lead",
    write: true,
    label: "Push Lead",
    group: "Monday — Lead Capture",
    description:
      "Capture visitor data and create a lead item on the default Monday board."
  },
  {
    name: "monday_search_items",
    label: "Search Items",
    group: "Monday — Items",
    description: "Search for items across boards by keyword."
  },
  {
    name: "monday_get_item",
    label: "Get Item",
    group: "Monday — Items",
    description: "Get full details for a board item by ID."
  },
  {
    name: "monday_create_item",
    write: true,
    label: "Create Item",
    group: "Monday — Items",
    description: "Create a new item on a board."
  },
  {
    name: "monday_update_item",
    write: true,
    label: "Update Item",
    group: "Monday — Items",
    description: "Update column values on a board item."
  },
  {
    name: "monday_get_column_values",
    label: "Get Column Values",
    group: "Monday — Items",
    description: "Get all column values for a board item."
  },
  {
    name: "monday_move_item",
    label: "Move Item",
    group: "Monday — Items",
    description: "Move a board item to a different group."
  },
  {
    name: "monday_archive_item",
    label: "Archive Item",
    group: "Monday — Items",
    description: "Archive a completed or resolved item."
  },
  {
    name: "monday_set_status",
    write: true,
    label: "Set Status",
    group: "Monday — Items",
    description: "Set the status column of a board item."
  },
  {
    name: "monday_assign_user",
    write: true,
    label: "Assign User",
    group: "Monday — Items",
    description: "Assign a team member to a board item."
  },
  {
    name: "monday_delete_item",
    label: "Delete Item",
    group: "Monday — Items",
    description: "Permanently delete a board item.",
    destructive: true
  },
  {
    name: "monday_create_update",
    write: true,
    label: "Post Update",
    group: "Monday — Updates",
    description: "Post a timeline update or comment to a board item."
  },
  {
    name: "monday_get_updates",
    label: "Get Updates",
    group: "Monday — Updates",
    description: "Get recent updates and comments on a board item."
  },
  {
    name: "monday_get_board",
    label: "Get Board",
    group: "Monday — Boards",
    description: "Get board structure — columns, groups, and configuration."
  },
  {
    name: "monday_list_boards",
    label: "List Boards",
    group: "Monday — Boards",
    description: "List all available Monday.com boards."
  },
  {
    name: "monday_create_subitem",
    write: true,
    label: "Create Subitem",
    group: "Monday — Subitems",
    description: "Create a subitem under an existing board item."
  },
  {
    name: "monday_get_subitems",
    label: "Get Subitems",
    group: "Monday — Subitems",
    description: "Get all subitems for a board item."
  },
  {
    name: "monday_get_users",
    label: "Get Users",
    group: "Monday — Users",
    description: "Get list of board members available for assignment."
  },
  {
    name: "monday_notify_user",
    label: "Notify User",
    group: "Monday — Users",
    description: "Send an in-app notification to a team member."
  },

  // ── Notion ────────────────────────────────────────────────────────────────
  {
    name: "notion_search",
    label: "Search Workspace",
    group: "Notion — Knowledge Base",
    description:
      "Searches your Notion workspace for relevant pages and documentation before answering product/policy/pricing questions."
  },
  {
    name: "notion_get_page",
    label: "Get Page",
    group: "Notion — Knowledge Base",
    description:
      "Fetches a specific Notion page's title, URL, and properties by page ID."
  },
  {
    name: "notion_get_page_content",
    label: "Get Page Content",
    group: "Notion — Knowledge Base",
    description:
      "Fetches the full text content of a Notion page (paragraphs, headings, lists)."
  },
  {
    name: "notion_get_comments",
    label: "Get Page Comments",
    group: "Notion — Knowledge Base",
    description: "Fetches the comment thread on a Notion page."
  },
  {
    name: "notion_query_database",
    label: "Query Database",
    group: "Notion — Databases",
    description:
      "Returns rows from a Notion database, optionally filtered by keyword."
  },
  {
    name: "notion_get_database_schema",
    label: "Get Database Schema",
    group: "Notion — Databases",
    description: "Returns a Notion database's property names and types."
  },
  {
    name: "notion_list_databases",
    label: "List Databases",
    group: "Notion — Databases",
    description: "Lists the Notion databases this integration can see."
  },
  {
    name: "notion_find_database_item",
    label: "Find Database Item",
    group: "Notion — Databases",
    description:
      "Finds a single row in a Notion database whose property exactly matches a value."
  },
  {
    name: "notion_list_users",
    label: "List Workspace Users",
    group: "Notion — Databases",
    description: "Lists the members of the connected Notion workspace."
  },
  {
    name: "notion_create_lead",
    label: "Create Lead Record",
    group: "Notion Write — Leads",
    description:
      "Creates a lead record (name, email, phone, company, conversation summary) in your configured Notion leads database.",
    write: true
  },
  {
    name: "notion_create_page",
    label: "Create Page",
    group: "Notion Write — Pages",
    description:
      "Creates a new page in a Notion database or as a sub-page under an existing page.",
    write: true
  },
  {
    name: "notion_update_page",
    label: "Update Page",
    group: "Notion Write — Pages",
    description:
      "Updates one or more property values on an existing Notion database row.",
    write: true
  },
  {
    name: "notion_append_note",
    label: "Append Note",
    group: "Notion Write — Pages",
    description:
      "Appends a plain-text paragraph to the body of an existing Notion page.",
    write: true
  },
  {
    name: "notion_create_comment",
    label: "Add Comment",
    group: "Notion Write — Pages",
    description: "Adds a comment to a Notion page's comment thread.",
    write: true
  },
  {
    name: "notion_archive_page",
    label: "Archive Page",
    group: "Notion Enterprise — Pages",
    description: "Archives (soft-deletes) a Notion page.",
    destructive: true
  },

  // ── SAP Business One ─────────────────────────────────────────────────────
  {
    name: "sap_get_business_partner",
    label: "Get Business Partner",
    group: "SAP — Business Partners",
    description:
      "Fetches a specific SAP business partner (customer or vendor) record."
  },
  {
    name: "sap_search_business_partners",
    label: "Search Business Partners",
    group: "SAP — Business Partners",
    description:
      "Searches SAP business partners by name or other identifying details."
  },
  {
    name: "sap_add_contact_person",
    label: "Add Contact Person",
    group: "SAP Write — Business Partners",
    description: "Adds a new contact person to a SAP business partner record.",
    write: true
  },
  {
    name: "sap_get_contact_persons",
    label: "Get Contact Persons",
    group: "SAP — Business Partners",
    description:
      "Fetches the contact people associated with a SAP business partner."
  },
  {
    name: "sap_create_activity",
    label: "Create Activity",
    group: "SAP Write — Activities",
    description:
      "Logs a new activity (call, meeting, note) against a SAP business partner.",
    write: true
  },
  {
    name: "sap_get_activities",
    label: "Get Activities",
    group: "SAP — Activities",
    description: "Fetches logged activities for a SAP business partner."
  },
  {
    name: "sap_create_quotation",
    label: "Create Quotation",
    group: "SAP Write — Quotes",
    description: "Creates a new SAP sales quotation.",
    write: true
  },
  {
    name: "sap_get_quotation",
    label: "Get Quotation",
    group: "SAP — Quotes",
    description: "Fetches a specific SAP sales quotation by ID."
  },
  {
    name: "sap_get_quotations",
    label: "Get Quotations",
    group: "SAP — Quotes",
    description: "Fetches SAP sales quotations for a business partner."
  },
  {
    name: "sap_create_sales_order",
    label: "Create Sales Order",
    group: "SAP Write — Sales Orders",
    description: "Creates a new SAP sales order.",
    write: true
  },
  {
    name: "sap_get_sales_order",
    label: "Get Sales Order",
    group: "SAP — Sales Orders",
    description: "Fetches a specific SAP sales order by ID."
  },
  {
    name: "sap_get_orders_by_partner",
    label: "Get Orders by Partner",
    group: "SAP — Sales Orders",
    description: "Fetches all SAP sales orders for a given business partner."
  },
  {
    name: "sap_search_sales_orders",
    label: "Search Sales Orders",
    group: "SAP — Sales Orders",
    description: "Searches SAP sales orders by criteria such as status or date."
  },
  {
    name: "sap_create_service_call",
    label: "Create Service Call",
    group: "SAP Write — Service",
    description: "Opens a new SAP service call (support ticket).",
    write: true
  },
  {
    name: "sap_get_service_calls",
    label: "Get Service Calls",
    group: "SAP — Service",
    description: "Fetches SAP service calls for a business partner."
  },
  {
    name: "sap_add_service_call_note",
    label: "Add Service Call Note",
    group: "SAP Write — Service",
    description: "Adds a note to an existing SAP service call.",
    write: true
  },
  {
    name: "sap_update_service_call",
    label: "Update Service Call",
    group: "SAP Write — Service",
    description:
      "Updates the status or details of an existing SAP service call.",
    write: true
  },
  {
    name: "sap_get_ap_invoices",
    label: "Get AP Invoices",
    group: "SAP — Finance",
    description: "Fetches SAP accounts-payable (vendor) invoices."
  },
  {
    name: "sap_get_open_invoices",
    label: "Get Open Invoices",
    group: "SAP — Finance",
    description: "Fetches unpaid SAP invoices for a business partner."
  },
  {
    name: "sap_get_invoice",
    label: "Get Invoice",
    group: "SAP — Finance",
    description: "Fetches a specific SAP invoice by ID."
  },
  {
    name: "sap_get_credit_notes",
    label: "Get Credit Notes",
    group: "SAP — Finance",
    description: "Fetches SAP credit notes for a business partner."
  },
  {
    name: "sap_get_incoming_payments",
    label: "Get Incoming Payments",
    group: "SAP — Finance",
    description:
      "Fetches recorded incoming SAP payments for a business partner."
  },
  {
    name: "sap_get_item",
    label: "Get Item",
    group: "SAP — Inventory",
    description: "Fetches a specific SAP inventory item by code."
  },
  {
    name: "sap_search_items",
    label: "Search Items",
    group: "SAP — Inventory",
    description: "Searches the SAP item master by name or code."
  },
  {
    name: "sap_get_stock",
    label: "Get Stock Level",
    group: "SAP — Inventory",
    description: "Fetches the current stock level for a SAP item."
  },
  {
    name: "sap_get_deliveries",
    label: "Get Deliveries",
    group: "SAP — Logistics",
    description: "Fetches SAP delivery documents for a business partner."
  },
  {
    name: "sap_get_delivery",
    label: "Get Delivery",
    group: "SAP — Logistics",
    description: "Fetches a specific SAP delivery document by ID."
  },
  {
    name: "sap_get_goods_receipts",
    label: "Get Goods Receipts",
    group: "SAP — Logistics",
    description: "Fetches SAP goods-receipt documents."
  },
  {
    name: "sap_get_returns",
    label: "Get Returns",
    group: "SAP — Logistics",
    description: "Fetches SAP return documents for a business partner."
  },
  {
    name: "sap_get_production_order",
    label: "Get Production Order",
    group: "SAP — Production",
    description: "Fetches a specific SAP production order by ID."
  },
  {
    name: "sap_get_production_orders",
    label: "Get Production Orders",
    group: "SAP — Production",
    description: "Fetches SAP production orders by status or item."
  },
  {
    name: "sap_get_purchase_orders",
    label: "Get Purchase Orders",
    group: "SAP — Purchasing",
    description: "Fetches SAP purchase orders for a vendor."
  },
  {
    name: "sap_search_vendors",
    label: "Search Vendors",
    group: "SAP — Purchasing",
    description: "Searches SAP vendor business partners by name."
  },

  // ── ServiceNow ────────────────────────────────────────────────────────────
  {
    name: "servicenow_get_incident",
    label: "Get Incident",
    group: "ServiceNow — Incidents",
    description: "Fetches a specific ServiceNow incident by number."
  },
  {
    name: "servicenow_create_incident",
    label: "Create Incident",
    group: "ServiceNow Write — Incidents",
    description: "Creates a new ServiceNow incident.",
    write: true
  },
  {
    name: "servicenow_close_incident",
    label: "Close Incident",
    group: "ServiceNow Write — Incidents",
    description: "Closes an existing ServiceNow incident.",
    write: true
  },
  {
    name: "servicenow_escalate_incident",
    label: "Escalate Incident",
    group: "ServiceNow Write — Incidents",
    description:
      "Escalates a ServiceNow incident to a higher priority or assignment group.",
    write: true
  },
  {
    name: "servicenow_update_incident",
    label: "Update Incident",
    group: "ServiceNow Write — Incidents",
    description: "Updates fields on an existing ServiceNow incident.",
    write: true
  },
  {
    name: "servicenow_search_incidents",
    label: "Search Incidents",
    group: "ServiceNow — Incidents",
    description: "Searches ServiceNow incidents by keyword or criteria."
  },
  {
    name: "servicenow_get_incident_updates",
    label: "Get Incident Updates",
    group: "ServiceNow — Incidents",
    description: "Fetches the update/comment history on a ServiceNow incident."
  },
  {
    name: "servicenow_get_user_incidents",
    label: "Get User's Incidents",
    group: "ServiceNow — Incidents",
    description:
      "Fetches open ServiceNow incidents reported by a specific user."
  },
  {
    name: "servicenow_add_work_note",
    label: "Add Work Note",
    group: "ServiceNow Write — Incidents",
    description:
      "Adds an internal work note to a ServiceNow incident, problem, or request.",
    write: true
  },
  {
    name: "servicenow_get_asset",
    label: "Get Asset",
    group: "ServiceNow — Assets",
    description: "Fetches a specific ServiceNow asset record."
  },
  {
    name: "servicenow_search_assets",
    label: "Search Assets",
    group: "ServiceNow — Assets",
    description: "Searches ServiceNow assets by owner, type, or tag."
  },
  {
    name: "servicenow_get_configuration_item",
    label: "Get Configuration Item",
    group: "ServiceNow — Assets",
    description: "Fetches a ServiceNow configuration item (CI) record."
  },
  {
    name: "servicenow_get_service_request",
    label: "Get Service Request",
    group: "ServiceNow — Requests",
    description: "Fetches a specific ServiceNow service request."
  },
  {
    name: "servicenow_create_service_request",
    label: "Create Service Request",
    group: "ServiceNow Write — Requests",
    description: "Creates a new ServiceNow service request from the catalog.",
    write: true
  },
  {
    name: "servicenow_get_request_status",
    label: "Get Request Status",
    group: "ServiceNow — Requests",
    description: "Checks the current status of a ServiceNow request."
  },
  {
    name: "servicenow_get_catalog_items",
    label: "Get Catalog Items",
    group: "ServiceNow — Requests",
    description: "Fetches available items from the ServiceNow service catalog."
  },
  {
    name: "servicenow_approve_request",
    label: "Approve Request",
    group: "ServiceNow Write — Requests",
    description:
      "Approves a pending ServiceNow request awaiting the visitor's sign-off.",
    write: true
  },
  {
    name: "servicenow_get_pending_approvals",
    label: "Get Pending Approvals",
    group: "ServiceNow — Requests",
    description: "Fetches ServiceNow requests awaiting the visitor's approval."
  },
  {
    name: "servicenow_create_facilities_request",
    label: "Create Facilities Request",
    group: "ServiceNow Write — Requests",
    description: "Submits a new ServiceNow facilities request.",
    write: true
  },
  {
    name: "servicenow_get_hr_case",
    label: "Get HR Case",
    group: "ServiceNow — HR",
    description: "Fetches a specific ServiceNow HR case."
  },
  {
    name: "servicenow_create_hr_case",
    label: "Create HR Case",
    group: "ServiceNow Write — HR",
    description: "Creates a new ServiceNow HR case.",
    write: true
  },
  {
    name: "servicenow_get_change_request",
    label: "Get Change Request",
    group: "ServiceNow — Change & Problem",
    description: "Fetches a specific ServiceNow change request."
  },
  {
    name: "servicenow_search_changes",
    label: "Search Changes",
    group: "ServiceNow — Change & Problem",
    description: "Searches ServiceNow change requests by criteria."
  },
  {
    name: "servicenow_get_upcoming_changes",
    label: "Get Upcoming Changes",
    group: "ServiceNow — Change & Problem",
    description: "Fetches scheduled upcoming ServiceNow changes."
  },
  {
    name: "servicenow_get_problem",
    label: "Get Problem",
    group: "ServiceNow — Change & Problem",
    description: "Fetches a specific ServiceNow problem record."
  },
  {
    name: "servicenow_search_problems",
    label: "Search Problems",
    group: "ServiceNow — Change & Problem",
    description: "Searches ServiceNow problem records by criteria."
  },
  {
    name: "servicenow_get_maintenance_windows",
    label: "Get Maintenance Windows",
    group: "ServiceNow — Change & Problem",
    description: "Fetches scheduled ServiceNow maintenance windows."
  },
  {
    name: "servicenow_check_service_status",
    label: "Check Service Status",
    group: "ServiceNow — Status",
    description:
      "Checks the current health/status of a ServiceNow-tracked service."
  },
  {
    name: "servicenow_get_active_outages",
    label: "Get Active Outages",
    group: "ServiceNow — Status",
    description: "Fetches currently active outages tracked in ServiceNow."
  },
  {
    name: "servicenow_get_on_call",
    label: "Get On-Call",
    group: "ServiceNow — Status",
    description:
      "Fetches who is currently on-call for a ServiceNow assignment group."
  },
  {
    name: "servicenow_get_open_tasks",
    label: "Get Open Tasks",
    group: "ServiceNow — Status",
    description: "Fetches open ServiceNow tasks assigned to a user or group."
  },
  {
    name: "servicenow_get_assignment_groups",
    label: "Get Assignment Groups",
    group: "ServiceNow — Status",
    description: "Fetches available ServiceNow assignment groups."
  },
  {
    name: "servicenow_get_article",
    label: "Get KB Article",
    group: "ServiceNow — Knowledge",
    description: "Fetches a specific ServiceNow knowledge base article."
  },
  {
    name: "servicenow_search_knowledge",
    label: "Search Knowledge Base",
    group: "ServiceNow — Knowledge",
    description: "Searches the ServiceNow knowledge base by keyword."
  },
  {
    name: "servicenow_get_user",
    label: "Get User",
    group: "ServiceNow — Knowledge",
    description: "Fetches a ServiceNow user record."
  },

  // ── Pipedrive ─────────────────────────────────────────────────────────────
  {
    name: "pipedrive_get_person",
    label: "Get Person",
    group: "Pipedrive — People",
    description: "Fetches a specific Pipedrive person record."
  },
  {
    name: "pipedrive_create_person",
    write: true,
    label: "Create Person",
    group: "Pipedrive — People",
    description: "Creates a new Pipedrive person record."
  },
  {
    name: "pipedrive_search_person",
    label: "Search Person",
    group: "Pipedrive — People",
    description: "Searches Pipedrive people by name."
  },
  {
    name: "pipedrive_search_person_by_email",
    label: "Search Person by Email",
    group: "Pipedrive — People",
    description: "Searches Pipedrive people by email address."
  },
  {
    name: "pipedrive_search_organizations",
    label: "Search Organizations",
    group: "Pipedrive — People",
    description: "Searches Pipedrive organizations by name."
  },
  {
    name: "pipedrive_get_activities",
    label: "Get Person Activities",
    group: "Pipedrive — People",
    description: "Fetches logged activities for a Pipedrive person."
  },
  {
    name: "pipedrive_add_activity",
    write: true,
    label: "Add Activity",
    group: "Pipedrive — People",
    description:
      "Logs a new activity (call, meeting, task) against a Pipedrive person or deal."
  },
  {
    name: "pipedrive_add_note",
    write: true,
    label: "Add Note",
    group: "Pipedrive — People",
    description: "Adds a note to a Pipedrive person or deal."
  },
  {
    name: "pipedrive_get_person_deals",
    label: "Get Person Deals",
    group: "Pipedrive — Deals",
    description: "Fetches all Pipedrive deals associated with a person."
  },
  {
    name: "pipedrive_search_deals",
    label: "Search Deals",
    group: "Pipedrive — Deals",
    description: "Searches Pipedrive deals by criteria."
  },
  {
    name: "pipedrive_create_deal",
    write: true,
    label: "Create Deal",
    group: "Pipedrive — Deals",
    description: "Creates a new Pipedrive deal."
  },
  {
    name: "pipedrive_update_deal",
    write: true,
    label: "Update Deal",
    group: "Pipedrive — Deals",
    description: "Updates fields on an existing Pipedrive deal."
  },
  {
    name: "pipedrive_won_deal",
    label: "Mark Deal as Won",
    group: "Pipedrive Sales — Deals",
    description: "Marks a Pipedrive deal as won.",
    destructive: true
  },
  {
    name: "pipedrive_lost_deal",
    label: "Mark Deal as Lost",
    group: "Pipedrive Sales — Deals",
    description: "Marks a Pipedrive deal as lost.",
    destructive: true
  },

  // ── QuickBooks Online ─────────────────────────────────────────────────────
  {
    name: "qbo_get_customer",
    label: "Get Customer",
    group: "QuickBooks — Customers",
    description: "Fetches a specific QuickBooks Online customer record."
  },
  {
    name: "qbo_search_customer",
    label: "Search Customer",
    group: "QuickBooks — Customers",
    description: "Searches QuickBooks Online customers by name or email."
  },
  {
    name: "qbo_create_customer",
    label: "Create Customer",
    group: "QuickBooks Write — Customers",
    description: "Creates a new QuickBooks Online customer record.",
    write: true
  },
  {
    name: "qbo_get_balance",
    label: "Get Balance",
    group: "QuickBooks — Invoicing",
    description:
      "Fetches a customer's current outstanding balance in QuickBooks Online."
  },
  {
    name: "qbo_get_invoices",
    label: "Get Invoices",
    group: "QuickBooks — Invoicing",
    description: "Fetches a customer's QuickBooks Online invoices."
  },
  {
    name: "qbo_get_invoice_detail",
    label: "Get Invoice Detail",
    group: "QuickBooks — Invoicing",
    description:
      "Fetches full line-item detail for a specific QuickBooks Online invoice."
  },
  {
    name: "qbo_get_estimates",
    label: "Get Estimates",
    group: "QuickBooks — Invoicing",
    description: "Fetches a customer's QuickBooks Online estimates/quotes."
  },
  {
    name: "qbo_get_payments",
    label: "Get Payments",
    group: "QuickBooks — Invoicing",
    description: "Fetches a customer's recorded QuickBooks Online payments."
  },

  // ── Accela (Government/Permitting) ────────────────────────────────────────
  {
    name: "accela_get_record",
    label: "Get Permit / Record",
    group: "Accela — Records",
    description: "Fetches a specific Accela permit/record by ID."
  },
  {
    name: "accela_search_records",
    label: "Search Permits & Licenses",
    group: "Accela — Records",
    description: "Searches Accela permits and licenses by criteria."
  },
  {
    name: "accela_search_by_address",
    label: "Search Records by Address",
    group: "Accela — Records",
    description: "Searches Accela records tied to a specific property address."
  },
  {
    name: "accela_create_record",
    label: "Create Permit / Record",
    group: "Accela Write — Records",
    description: "Submits a new Accela permit/record application.",
    write: true
  },
  {
    name: "accela_update_record",
    label: "Update Permit / Record",
    group: "Accela Write — Records",
    description: "Updates fields on an existing Accela permit/record.",
    write: true
  },
  {
    name: "accela_get_record_status",
    label: "Get Record / Permit Status",
    group: "Accela — Records",
    description: "Checks the current status of an Accela permit/record."
  },
  {
    name: "accela_get_record_types",
    label: "Get Available Permit Types",
    group: "Accela — Records",
    description:
      "Fetches the permit/record types available in this agency's Accela instance."
  },
  {
    name: "accela_get_record_documents",
    label: "Get Record Documents",
    group: "Accela — Records",
    description: "Fetches documents attached to an Accela record."
  },
  {
    name: "accela_get_record_workflow",
    label: "Get Record Workflow Tasks",
    group: "Accela — Records",
    description: "Fetches the workflow/review tasks on an Accela record."
  },
  {
    name: "accela_get_record_conditions",
    label: "Get Record Conditions / Holds",
    group: "Accela — Records",
    description:
      "Fetches conditions or holds blocking progress on an Accela record."
  },
  {
    name: "accela_add_record_comment",
    label: "Add Comment to Record",
    group: "Accela Write — Records",
    description: "Adds a comment to an Accela record.",
    write: true
  },
  {
    name: "accela_get_fees",
    label: "Get Permit Fees",
    group: "Accela — Records",
    description: "Fetches the fees owed or paid on an Accela permit/record."
  },
  {
    name: "accela_get_parcel_info",
    label: "Get Parcel / Property Info",
    group: "Accela — Records",
    description:
      "Fetches Accela parcel/property information for an address or parcel number."
  },
  {
    name: "accela_get_record_contacts",
    label: "Get Record Contacts",
    group: "Accela — Contacts",
    description: "Fetches the contacts associated with an Accela record."
  },
  {
    name: "accela_get_contact",
    label: "Get Contact Details",
    group: "Accela — Contacts",
    description: "Fetches a specific Accela citizen contact record."
  },
  {
    name: "accela_create_contact",
    label: "Create Citizen Contact",
    group: "Accela Write — Contacts",
    description: "Creates a new Accela citizen contact record.",
    write: true
  },
  {
    name: "accela_update_contact",
    label: "Update Contact Info",
    group: "Accela Write — Contacts",
    description: "Updates fields on an existing Accela citizen contact.",
    write: true
  },
  {
    name: "accela_search_contacts",
    label: "Search Citizen Contacts",
    group: "Accela — Contacts",
    description: "Searches Accela citizen contacts by name or address."
  },
  {
    name: "accela_get_contact_addresses",
    label: "Get Contact Addresses",
    group: "Accela — Contacts",
    description: "Fetches addresses on file for an Accela contact."
  },
  {
    name: "accela_get_contact_licenses",
    label: "Get Contact's Professional Licenses",
    group: "Accela — Contacts",
    description: "Fetches professional licenses held by an Accela contact."
  },
  {
    name: "accela_get_contact_records",
    label: "Get Contact's Permits & Records",
    group: "Accela — Contacts",
    description: "Fetches all Accela permits/records associated with a contact."
  },
  {
    name: "accela_search_professionals",
    label: "Search Licensed Professionals",
    group: "Accela — Contacts",
    description:
      "Searches Accela for licensed professionals (contractors, inspectors) by criteria."
  },
  {
    name: "accela_get_inspection_types",
    label: "Get Available Inspection Types",
    group: "Accela — Inspections",
    description:
      "Fetches the inspection types available for a given permit type."
  },
  {
    name: "accela_schedule_inspection",
    label: "Schedule Inspection",
    group: "Accela Write — Inspections",
    description: "Schedules a new inspection on an Accela permit/record.",
    write: true
  },
  {
    name: "accela_get_record_inspections",
    label: "Get Inspections for Record",
    group: "Accela — Inspections",
    description:
      "Fetches scheduled and completed inspections for an Accela record."
  },
  {
    name: "accela_get_service_request_types",
    label: "Get 311 Issue Categories",
    group: "Accela — 311 Requests",
    description: "Fetches available 311 service request categories."
  },
  {
    name: "accela_create_service_request",
    label: "Submit 311 Service Request",
    group: "Accela Write — 311 Requests",
    description:
      "Submits a new 311 service request (pothole, noise complaint, etc.).",
    write: true
  },
  {
    name: "accela_get_service_request_status",
    label: "Check 311 Request Status",
    group: "Accela — 311 Requests",
    description: "Checks the status of a submitted 311 service request."
  },
  {
    name: "accela_search_service_requests_by_address",
    label: "Search 311 Requests by Address",
    group: "Accela — 311 Requests",
    description: "Searches 311 service requests tied to a specific address."
  },
  {
    name: "accela_get_agency_info",
    label: "Get Agency Info",
    group: "Accela — 311 Requests",
    description:
      "Fetches general information about the connected Accela agency."
  },

  // ── Platform — SMS & Email (require EnableBotSmsSkill / EnableBotEmailSkill) ──
  {
    name: "send_sms",
    write: true,
    label: "Send SMS to caller/visitor",
    group: "Platform — SMS & Email",
    description:
      "AI sends an SMS directly to the caller or visitor. Useful on IVR to text a confirmation, link, or follow-up. Requires SMS capability to be enabled on your account."
  },
  {
    name: "send_sms_link",
    write: true,
    label: "Send SMS with link",
    group: "Platform — SMS & Email",
    description:
      "AI texts a URL or checkout link to the caller. On IVR use this instead of reading a URL aloud. Requires SMS capability."
  },
  {
    name: "send_email",
    write: true,
    label: "Send email",
    group: "Platform — SMS & Email",
    description:
      "AI sends an email on behalf of the customer or the business. Requires email capability to be enabled on your account."
  },
  {
    name: "send_email_link",
    write: true,
    label: "Send email with link",
    group: "Platform — SMS & Email",
    description:
      "AI sends an email containing a URL or asset link. Requires email/SMS capability."
  },

  // ── Platform — IVR Voice (require EnableIvr / EnableIvrBridgeCall / EnableCtc) ─
  {
    name: "bridge_call_to_agent",
    label: "Transfer call to agent (bridge)",
    group: "Platform — IVR Voice",
    description:
      "AI cold-transfers the caller to a live agent phone number. The AI conversation ends and the call is bridged to the agent. Requires IVR Bridge Call capability."
  },

  // ── Callback Scheduling — Voice & Tickets (require EnableCallbackScheduling; unified orchestrator, Stage 6) ─
  // offer_callback/schedule_callback tool-definitions are unchanged (ToolName strings match
  // OfferCallbackSkill.cs/ScheduleCallbackSkill.cs in velaro-messaging) — only their internal
  // ExecuteAsync repointed to ICallbackOrchestrationService per callback-scheduling-architecture.md §10.
  // Moved out of "Platform — IVR Voice" so the skill picker gates on EnableCallbackScheduling
  // (the flag that now actually governs whether the orchestrator will fulfill these requests)
  // instead of the old EnableIvr Subscription flag, which never actually gated this group at
  // the picker level (see IntegrationSkillsSection.tsx's split(" — ")[0] prefix-matching note).
  {
    name: "offer_callback",
    label: "Offer callback to caller",
    group: "Callback Scheduling — Voice & Tickets",
    description:
      "AI offers to call the customer back when an agent is available. Works on IVR, Click-to-Call, and avatar conversations."
  },
  {
    name: "schedule_callback",
    write: true,
    label: "Schedule callback (smart routing)",
    group: "Callback Scheduling — Voice & Tickets",
    description:
      "AI schedules a callback at a specific time using smart routing. Requires Click-to-Call Smart Scheduling capability."
  },

  // ── Surveys (require EnableAiSurveyTrigger; velaro_trigger_survey / TriggerSurveySkill.cs) ─
  {
    name: "velaro_trigger_survey",
    label: "Trigger survey mid-conversation",
    group: "Surveys — AI-Initiated",
    description:
      "AI proactively launches an existing survey inline in the conversation (e.g. after resolving an issue). Delivers the same way as the agent-manual 'send survey' composer action."
  },

  // ── Square ─────────────────────────────────────────────────────────────────
  {
    name: "square_check_availability",
    label: "Check availability",
    group: "Square — Appointments",
    description: "Find open appointment slots for a service."
  },
  {
    name: "square_book_appointment",
    write: true,
    label: "Book appointment",
    group: "Square — Appointments",
    description: "Create a Square booking for a visitor."
  },
  {
    name: "square_cancel_appointment",
    label: "Cancel appointment",
    group: "Square — Appointments",
    description: "Cancel an existing booking.",
    destructive: true
  },
  {
    name: "square_reschedule_appointment",
    write: true,
    label: "Reschedule appointment",
    group: "Square — Appointments",
    description: "Cancel and rebook at a new time."
  },
  {
    name: "square_list_services",
    label: "List services",
    group: "Square — Appointments",
    description: "Return all bookable services from the catalog."
  },
  {
    name: "square_get_customer_bookings",
    label: "Get customer bookings",
    group: "Square — Appointments",
    description: "Look up a customer's upcoming and past bookings."
  },
  {
    name: "square_get_customer",
    label: "Get customer",
    group: "Square — Appointments",
    description: "Look up a Square customer by phone or email."
  },
  {
    name: "square_create_customer",
    write: true,
    label: "Create customer",
    group: "Square — Appointments",
    description: "Create a new Square customer record."
  },
  {
    name: "square_search_catalog",
    label: "Search catalog",
    group: "Square — Catalog & Inventory",
    description: "Search Square catalog by keyword."
  },
  {
    name: "square_get_catalog_item",
    label: "Get catalog item",
    group: "Square — Catalog & Inventory",
    description: "Fetch full item details."
  },
  {
    name: "square_list_categories",
    label: "List categories",
    group: "Square — Catalog & Inventory",
    description: "Return all product/service categories."
  },
  {
    name: "square_check_inventory",
    label: "Check inventory",
    group: "Square — Catalog & Inventory",
    description: "Check current stock count for a catalog item."
  },
  {
    name: "square_create_checkout_link",
    write: true,
    label: "Create checkout link",
    group: "Square — Catalog & Inventory",
    description: "Generate a hosted Square checkout link for a cart."
  },
  {
    name: "square_search_orders",
    label: "Search orders",
    group: "Square — Orders",
    description: "Search a customer's orders."
  },
  {
    name: "square_get_order",
    label: "Get order details",
    group: "Square — Orders",
    description: "Retrieve full order details."
  },
  {
    name: "square_cancel_order",
    label: "Cancel order",
    group: "Square — Orders",
    description: "Request order cancellation.",
    destructive: true
  },
  {
    name: "square_update_customer",
    write: true,
    label: "Update customer",
    group: "Square — Orders",
    description: "Update customer profile fields."
  },
  {
    name: "square_create_refund",
    label: "Create refund",
    group: "Square — Orders",
    description: "Issue a full or partial refund on a Square payment.",
    destructive: true
  },
  {
    name: "square_get_customer_segments",
    label: "Get customer segments",
    group: "Square — Orders",
    description: "Return the loyalty/marketing segments a customer belongs to."
  },
  {
    name: "square_get_loyalty_balance",
    label: "Get loyalty balance",
    group: "Square — Loyalty & Gifts",
    description: "Return a customer's current loyalty point balance."
  },
  {
    name: "square_get_loyalty_rewards",
    label: "Get loyalty rewards",
    group: "Square — Loyalty & Gifts",
    description: "List available loyalty rewards for redemption."
  },
  {
    name: "square_redeem_loyalty_reward",
    label: "Redeem loyalty reward",
    group: "Square — Loyalty & Gifts",
    description: "Redeem a loyalty reward for a customer.",
    destructive: true
  },
  {
    name: "square_check_gift_card",
    label: "Check gift card",
    group: "Square — Loyalty & Gifts",
    description: "Look up a gift card balance."
  },
  {
    name: "square_get_payment_history",
    label: "Get payment history",
    group: "Square — Payments",
    description: "Retrieve recent payment history for a customer."
  },
  {
    name: "square_get_invoice",
    label: "Get invoice",
    group: "Square — Payments",
    description: "Retrieve a specific Square invoice by ID."
  },
  {
    name: "square_list_invoices",
    label: "List invoices",
    group: "Square — Payments",
    description: "List invoices for a customer."
  },
  {
    name: "square_get_subscription",
    label: "Get subscription",
    group: "Square — Payments",
    description: "Look up a customer's active Square subscription."
  },
  {
    name: "square_list_locations",
    label: "List locations",
    group: "Square — Payments",
    description: "Return the site's connected Square business locations."
  },

  // ── VAST Retail (Kerridge CS) tire shop DMS ───────────────────────────────
  {
    name: "vast_lookup_customer_phone",
    label: "Look up customer by phone",
    group: "VAST — Customer",
    description: "Find a VAST Retail customer by phone number."
  },
  {
    name: "vast_lookup_customer_email",
    label: "Look up customer by email",
    group: "VAST — Customer",
    description: "Find a VAST Retail customer by email address."
  },
  {
    name: "vast_get_vehicles",
    label: "Get customer vehicles",
    group: "VAST — Vehicles",
    description: "List vehicles on file for a customer with year/make/model."
  },
  {
    name: "vast_get_service_history",
    label: "Get service history",
    group: "VAST — Vehicles",
    description: "Recent service visits with dates, work done, and technician."
  },
  {
    name: "vast_lookup_oem_tire_size",
    label: "OEM tire size lookup",
    group: "VAST — Tires",
    description: "OEM tire spec by year/make/model/trim — front and rear sizes."
  },
  {
    name: "vast_search_tire_inventory",
    label: "Search tire inventory",
    group: "VAST — Tires",
    description: "Search inventory by size, brand, vehicle, or category."
  },
  {
    name: "vast_get_tire_pricing",
    label: "Get tire pricing",
    group: "VAST — Tires",
    description: "Retail, sale, and install price for a tire and quantity."
  },
  {
    name: "vast_get_inventory_level",
    label: "Check tire stock",
    group: "VAST — Tires",
    description: "Stock count at a specific location."
  },
  {
    name: "vast_get_promotions",
    label: "Active promotions",
    group: "VAST — Tires",
    description: "Current tire deals and manufacturer rebates."
  },
  {
    name: "vast_get_available_slots",
    label: "Available appointment times",
    group: "VAST — Appointments",
    description: "Open appointment slots for a service type and date range.",
    channelWarning:
      "On IVR, read slots aloud and let customer select by number."
  },
  {
    name: "vast_get_appointments",
    label: "Get customer appointments",
    group: "VAST — Appointments",
    description: "Customer's upcoming appointments."
  },
  {
    name: "vast_book_appointment",
    write: true,
    label: "Book appointment",
    group: "VAST — Appointments",
    description: "Book a service appointment.",
    channelWarning: "Confirm date/time aloud before booking on IVR."
  },
  {
    name: "vast_cancel_appointment",
    label: "Cancel appointment",
    group: "VAST — Appointments",
    description: "Cancel an appointment.",
    destructive: true
  },
  {
    name: "vast_reschedule_appointment",
    write: true,
    label: "Reschedule appointment",
    group: "VAST — Appointments",
    description: "Move an appointment to a new slot."
  },
  {
    name: "vast_create_work_order",
    write: true,
    label: "Create work order",
    group: "VAST — Work Orders",
    description: "Open a work order for a customer and vehicle."
  },
  {
    name: "vast_get_work_order_status",
    label: "Work order status",
    group: "VAST — Work Orders",
    description: "Check repair/service status — 'Is my car ready?'"
  },
  {
    name: "vast_get_work_orders",
    label: "Get customer work orders",
    group: "VAST — Work Orders",
    description: "Recent work orders for a customer."
  },
  {
    name: "vast_create_estimate",
    write: true,
    label: "Create estimate",
    group: "VAST — Estimates",
    description: "Generate a service estimate with sub-total, tax, and total."
  },
  {
    name: "vast_get_estimate",
    label: "Get estimate",
    group: "VAST — Estimates",
    description: "Retrieve an existing estimate by ID."
  },
  {
    name: "vast_get_warranty_info",
    label: "Tire warranty info",
    group: "VAST — Warranty",
    description: "Warranty duration and expiry for a purchased tire."
  },

  // ── Webflow website builder ───────────────────────────────────────────────
  {
    name: "webflow_list_sites",
    label: "List connected sites",
    group: "Webflow — Sites",
    description: "List all Webflow sites connected to this account."
  },
  {
    name: "webflow_get_site",
    label: "Get site details",
    group: "Webflow — Sites",
    description:
      "Get details about a connected Webflow site including name and ID."
  },
  {
    name: "webflow_inject_widget",
    label: "Inject chat widget",
    group: "Webflow — Sites",
    description:
      "Inject the Velaro chat widget on a connected Webflow site via Custom Code API."
  },
  {
    name: "webflow_remove_widget",
    label: "Remove chat widget",
    group: "Webflow — Sites",
    description: "Remove the Velaro chat widget from a Webflow site.",
    destructive: true
  },
  {
    name: "webflow_list_forms",
    label: "List forms",
    group: "Webflow — Forms",
    description: "List all forms on a connected Webflow site."
  },
  {
    name: "webflow_get_form_submissions",
    label: "Get form submissions",
    group: "Webflow — Forms",
    description: "Retrieve recent submissions from a Webflow form."
  },

  // ── Calendly (expanded) ───────────────────────────────────────────────────
  {
    name: "calendly_get_event_types",
    label: "List event types",
    group: "Calendly — Scheduling",
    description: "List available Calendly meeting types with duration and URI."
  },
  {
    name: "calendly_create_scheduling_link",
    write: true,
    label: "Create scheduling link",
    group: "Calendly — Scheduling",
    description: "Create a single-use or reusable Calendly booking link.",
    channelWarning:
      "On IVR, add a Send SMS section to deliver the link to the customer's phone."
  },
  {
    name: "calendly_get_scheduled_events",
    label: "List scheduled events",
    group: "Calendly — Scheduling",
    description: "Get upcoming booked events for the connected account."
  },
  {
    name: "calendly_check_availability",
    label: "Check availability",
    group: "Calendly — Scheduling",
    description: "Return open time slots for an event type within a date range."
  },
  {
    name: "calendly_get_event_details",
    label: "Get event details",
    group: "Calendly — Scheduling",
    description:
      "Full details for a specific scheduled event including invitees and join link."
  },
  {
    name: "calendly_get_invitee",
    label: "Look up invitee",
    group: "Calendly — Scheduling",
    description:
      "Find a booked invitee by email — confirms meeting and provides cancel/reschedule URLs."
  },
  {
    name: "calendly_get_organization_members",
    label: "List team members",
    group: "Calendly — Scheduling",
    description: "List all hosts in the Calendly organization."
  },
  {
    name: "calendly_cancel_event",
    label: "Cancel event",
    group: "Calendly — Scheduling",
    description:
      "Cancel a scheduled Calendly meeting. Invitees are notified automatically.",
    destructive: true
  },
  {
    name: "calendly_apple_book_appointment",
    write: true,
    label: "Send appointment picker (Apple Messages)",
    group: "Calendly — Scheduling",
    description:
      "Fetch Calendly availability and send a native Apple Messages Time Picker in one step. Apple Messages only.",
    channelWarning: "Only works on Apple Messages conversations."
  },

  // ── MS Bookings ────────────────────────────────────────────────────────────
  {
    name: "ms_bookings_get_business",
    label: "Get business info",
    group: "MS Bookings — Business",
    description:
      "Get business name, phone, email, website, and address from Microsoft Bookings."
  },
  {
    name: "ms_bookings_get_services",
    label: "List services",
    group: "MS Bookings — Business",
    description:
      "List available booking services with duration and pricing from Microsoft Bookings."
  },
  {
    name: "ms_bookings_get_staff",
    label: "List staff",
    group: "MS Bookings — Business",
    description:
      "List available staff members who can be booked from Microsoft Bookings."
  },
  {
    name: "ms_bookings_check_availability",
    label: "Check availability",
    group: "MS Bookings — Scheduling",
    description:
      "Check available appointment slots for a service on a specific date."
  },
  {
    name: "ms_bookings_check_availability_range",
    label: "Check availability (date range)",
    group: "MS Bookings — Scheduling",
    description:
      'Get available slots across a date range (up to 14 days), grouped by day — for flexible timing like "next week".'
  },
  {
    name: "ms_bookings_create_appointment",
    write: true,
    label: "Book appointment",
    group: "MS Bookings — Appointments",
    description: "Book an appointment in Microsoft Bookings for a customer."
  },
  {
    name: "ms_bookings_get_appointment",
    label: "Get appointment",
    group: "MS Bookings — Appointments",
    description:
      "Look up details for an existing Microsoft Bookings appointment by ID."
  },
  {
    name: "ms_bookings_reschedule_appointment",
    write: true,
    label: "Reschedule appointment",
    group: "MS Bookings — Appointments",
    description:
      "Move an existing Microsoft Bookings appointment to a new date and time."
  },
  {
    name: "ms_bookings_cancel_appointment",
    label: "Cancel appointment",
    group: "MS Bookings — Appointments",
    description: "Cancel an existing Microsoft Bookings appointment.",
    destructive: true
  },
  {
    name: "ms_bookings_apple_book_appointment",
    write: true,
    label: "Send appointment picker (Apple Messages)",
    group: "MS Bookings — Appointments",
    description:
      "Fetch Microsoft Bookings availability and send a native Apple Messages Time Picker in one step. Apple Messages only.",
    channelWarning: "Only works on Apple Messages conversations."
  },

  // ── Make (Integromat) ─────────────────────────────────────────────────────
  {
    name: "make_call_automation",
    label: "Trigger automation",
    group: "Make — Automation",
    description:
      "Send a webhook event to a Make (Integromat) scenario to trigger any custom automation."
  },

  // ── ServiceTitan (field service — HVAC, plumbing, electrical) ─────────────
  {
    name: "servicetitan_search_customer",
    label: "Search customer",
    group: "ServiceTitan — Customers",
    description:
      "Search by phone, name, or email. Returns customer ID, contact info, and address."
  },
  {
    name: "servicetitan_get_job",
    label: "Get job details",
    group: "ServiceTitan — Jobs",
    description:
      "Retrieve job type, status, scheduled time, and assigned technician."
  },
  {
    name: "servicetitan_get_appointments",
    label: "Get appointments",
    group: "ServiceTitan — Jobs",
    description:
      "Upcoming scheduled appointments for a customer or specific job."
  },
  {
    name: "servicetitan_get_invoices",
    label: "Get invoices",
    group: "ServiceTitan — Billing",
    description: "Recent invoices with total, balance due, and payment status."
  },
  {
    name: "servicetitan_get_equipment",
    label: "Get installed equipment",
    group: "ServiceTitan — Equipment",
    description:
      "Equipment on file for the customer — furnace, AC, water heater, with serial and warranty."
  },
  {
    name: "servicetitan_create_job",
    write: true,
    label: "Create service job",
    group: "ServiceTitan — Jobs",
    description: "Schedule a new service call for an existing customer."
  },
  {
    name: "servicetitan_add_job_note",
    write: true,
    label: "Add job note",
    group: "ServiceTitan — Jobs",
    description:
      "Add a note to an existing job for the dispatcher or technician."
  },
  {
    name: "servicetitan_update_job_status",
    label: "Update job status",
    group: "ServiceTitan — Jobs",
    description: "Cancel, hold, or flag a job for rescheduling.",
    destructive: true
  },

  // ── Airtable (flexible CRM / SMB database) ────────────────────────────────
  {
    name: "airtable_list_bases",
    label: "List bases",
    group: "Airtable — Database",
    description: "List all Airtable bases the token has access to."
  },
  {
    name: "airtable_list_tables",
    label: "List tables",
    group: "Airtable — Database",
    description: "List tables and their fields inside an Airtable base."
  },
  {
    name: "airtable_find_contact",
    label: "Find contact",
    group: "Airtable — Records",
    description: "Search for a contact/customer by email or phone number."
  },
  {
    name: "airtable_search_records",
    label: "Search records",
    group: "Airtable — Records",
    description:
      "Search any table using an Airtable filterByFormula expression."
  },
  {
    name: "airtable_get_record",
    label: "Get record",
    group: "Airtable — Records",
    description: "Retrieve a specific record by its Airtable record ID."
  },
  {
    name: "airtable_create_record",
    write: true,
    label: "Create record",
    group: "Airtable — Records",
    description: "Create a new Airtable record in any table."
  },
  {
    name: "airtable_update_record",
    write: true,
    label: "Update record",
    group: "Airtable — Records",
    description:
      "Update specific fields on an existing Airtable record (PATCH)."
  },
  {
    name: "airtable_delete_record",
    label: "Delete record",
    group: "Airtable — Records",
    description: "Permanently delete an Airtable record. Cannot be undone.",
    destructive: true
  },

  // ── Zoho CRM ──────────────────────────────────────────────────────────────────────
  {
    name: "zoho_search_contact",
    label: "Search contact",
    group: "Zoho — CRM",
    description: "Search Zoho CRM contacts by name, email, or phone."
  },
  {
    name: "zoho_get_contact",
    label: "Get contact",
    group: "Zoho — CRM",
    description: "Retrieve full details for a specific Zoho contact by ID."
  },
  {
    name: "zoho_get_contact_deals",
    label: "Get contact deals",
    group: "Zoho — CRM",
    description: "List deals associated with a Zoho contact."
  },
  {
    name: "zoho_search_leads",
    label: "Search leads",
    group: "Zoho — CRM",
    description: "Search Zoho leads by name, email, or company."
  },
  {
    name: "zoho_create_lead",
    write: true,
    label: "Create lead",
    group: "Zoho — CRM",
    description: "Create a new lead in Zoho CRM from the conversation."
  },
  {
    name: "zoho_update_lead",
    write: true,
    label: "Update lead",
    group: "Zoho — CRM",
    description: "Update fields on an existing Zoho lead."
  },
  {
    name: "zoho_create_contact",
    write: true,
    label: "Create contact",
    group: "Zoho — CRM",
    description: "Create a new contact record in Zoho CRM."
  },
  {
    name: "zoho_search_accounts",
    label: "Search accounts",
    group: "Zoho — CRM",
    description: "Search Zoho CRM accounts (companies) by name."
  },
  {
    name: "zoho_get_deal",
    label: "Get deal",
    group: "Zoho — CRM",
    description: "Retrieve details for a specific Zoho deal by ID."
  },
  {
    name: "zoho_add_note",
    write: true,
    label: "Add note",
    group: "Zoho — CRM",
    description: "Add a note to a Zoho CRM contact, lead, or deal."
  },
  {
    name: "zoho_won_deal",
    label: "Mark deal won",
    group: "Zoho — Sales",
    description: "Mark a Zoho deal as Won and record the closing date.",
    destructive: true
  },
  {
    name: "zoho_lost_deal",
    label: "Mark deal lost",
    group: "Zoho — Sales",
    description: "Mark a Zoho deal as Lost with a reason.",
    destructive: true
  },

  // ── Salesforce ────────────────────────────────────────────────────────────────────
  {
    name: "salesforce_get_account",
    label: "Get account",
    group: "Salesforce — CRM",
    description: "Retrieve a Salesforce account record by ID."
  },
  {
    name: "salesforce_get_account_contacts",
    label: "Get account contacts",
    group: "Salesforce — CRM",
    description: "List all contacts associated with a Salesforce account."
  },
  {
    name: "salesforce_get_contact",
    label: "Get contact",
    group: "Salesforce — CRM",
    description: "Retrieve a Salesforce contact record by ID."
  },
  {
    name: "salesforce_get_contact_cases",
    label: "Get contact cases",
    group: "Salesforce — CRM",
    description: "List support cases linked to a Salesforce contact."
  },
  {
    name: "salesforce_get_contact_opportunities",
    label: "Get contact opportunities",
    group: "Salesforce — CRM",
    description: "List opportunities linked to a Salesforce contact."
  },
  {
    name: "salesforce_get_lead",
    label: "Get lead",
    group: "Salesforce — CRM",
    description: "Retrieve a Salesforce lead record by ID."
  },
  {
    name: "salesforce_get_opportunity",
    label: "Get opportunity",
    group: "Salesforce — CRM",
    description: "Retrieve a Salesforce opportunity record by ID."
  },
  {
    name: "salesforce_get_case",
    label: "Get case",
    group: "Salesforce — CRM",
    description: "Retrieve a Salesforce support case by ID."
  },
  {
    name: "salesforce_get_tasks",
    label: "Get tasks",
    group: "Salesforce — CRM",
    description: "List Salesforce tasks for a contact or account."
  },
  {
    name: "salesforce_search_accounts",
    label: "Search accounts",
    group: "Salesforce — CRM",
    description: "Search Salesforce accounts by name or domain."
  },
  {
    name: "salesforce_search_contact",
    label: "Search contacts",
    group: "Salesforce — CRM",
    description: "Search Salesforce contacts by name, email, or phone."
  },
  {
    name: "salesforce_search_leads",
    label: "Search leads",
    group: "Salesforce — CRM",
    description: "Search Salesforce leads by name, email, or company."
  },
  {
    name: "salesforce_search_cases",
    label: "Search cases",
    group: "Salesforce — CRM",
    description: "Search Salesforce support cases by subject or contact."
  },
  {
    name: "salesforce_search_opportunities",
    label: "Search opportunities",
    group: "Salesforce — CRM",
    description: "Search Salesforce opportunities by name or account."
  },
  {
    name: "salesforce_create_contact",
    write: true,
    label: "Create contact",
    group: "Salesforce — Sales",
    description: "Create a new Salesforce contact record from the conversation."
  },
  {
    name: "salesforce_create_lead",
    write: true,
    label: "Create lead",
    group: "Salesforce — Sales",
    description: "Create a new Salesforce lead from the conversation."
  },
  {
    name: "salesforce_create_opportunity",
    write: true,
    label: "Create opportunity",
    group: "Salesforce — Sales",
    description: "Create a new Salesforce sales opportunity."
  },
  {
    name: "salesforce_create_task",
    write: true,
    label: "Create task",
    group: "Salesforce — Sales",
    description: "Create a follow-up task in Salesforce."
  },
  {
    name: "salesforce_update_contact",
    write: true,
    label: "Update contact",
    group: "Salesforce — Sales",
    description: "Update fields on a Salesforce contact record."
  },
  {
    name: "salesforce_update_lead",
    write: true,
    label: "Update lead",
    group: "Salesforce — Sales",
    description: "Update fields on a Salesforce lead record."
  },
  {
    name: "salesforce_update_opportunity",
    write: true,
    label: "Update opportunity",
    group: "Salesforce — Sales",
    description: "Update fields on a Salesforce opportunity."
  },
  {
    name: "salesforce_close_opportunity_won",
    label: "Mark opportunity won",
    group: "Salesforce — Sales",
    description: "Set a Salesforce opportunity stage to Closed Won.",
    destructive: true
  },
  {
    name: "salesforce_close_opportunity_lost",
    label: "Mark opportunity lost",
    group: "Salesforce — Sales",
    description: "Set a Salesforce opportunity stage to Closed Lost.",
    destructive: true
  },
  {
    name: "salesforce_add_note",
    write: true,
    label: "Add note",
    group: "Salesforce — Sales",
    description: "Add a note to a Salesforce contact, account, or opportunity."
  },
  {
    name: "salesforce_create_case",
    write: true,
    label: "Create case",
    group: "Salesforce — Service",
    description: "Create a new Salesforce support case from the conversation."
  },
  {
    name: "salesforce_update_case",
    write: true,
    label: "Update case",
    group: "Salesforce — Service",
    description: "Update fields on a Salesforce support case."
  },
  {
    name: "salesforce_close_case",
    label: "Close case",
    group: "Salesforce — Service",
    description: "Close a resolved Salesforce support case.",
    destructive: true
  },
  {
    name: "salesforce_add_case_comment",
    write: true,
    label: "Add case comment",
    group: "Salesforce — Service",
    description: "Add a public or internal comment to a Salesforce case."
  },
  {
    name: "salesforce_search_orders",
    label: "Search orders",
    group: "Salesforce — Commerce",
    description:
      "Search Salesforce Order records by order number or account name."
  },
  {
    name: "salesforce_get_order",
    label: "Get order details",
    group: "Salesforce — Commerce",
    description: "Full order details including line items, by Order Id."
  },
  {
    name: "salesforce_search_quotes",
    label: "Search quotes",
    group: "Salesforce — Commerce",
    description:
      "Search Salesforce Quote records by name or account/opportunity."
  },
  {
    name: "salesforce_get_quote",
    label: "Get quote details",
    group: "Salesforce — Commerce",
    description: "Full quote details including line items, by Quote Id."
  },
  {
    name: "salesforce_search_products",
    label: "Search products",
    group: "Salesforce — Commerce",
    description:
      "Search the Salesforce product catalog with standard-pricebook pricing."
  },
  {
    name: "salesforce_create_quote",
    write: true,
    label: "Create quote",
    group: "Salesforce — Commerce",
    description:
      "Create a draft quote linked to an existing Salesforce opportunity."
  },

  // ── Uplisting ─────────────────────────────────────────────────────────────────────
  {
    name: "uplisting_list_properties",
    label: "List properties",
    group: "Uplisting — Properties",
    description: "List all properties in the Uplisting account."
  },
  {
    name: "uplisting_get_property",
    label: "Get property details",
    group: "Uplisting — Properties",
    description: "Get full details for a specific Uplisting property."
  },
  {
    name: "uplisting_get_pricing",
    label: "Get pricing",
    group: "Uplisting — Properties",
    description: "Retrieve nightly pricing for a property."
  },
  {
    name: "uplisting_get_house_rules",
    label: "Get house rules",
    group: "Uplisting — Properties",
    description: "Retrieve house rules and policies for a property."
  },
  {
    name: "uplisting_get_checkin_instructions",
    label: "Get check-in instructions",
    group: "Uplisting — Properties",
    description: "Retrieve guest check-in instructions for a property."
  },
  {
    name: "uplisting_get_nearby_properties",
    label: "Get nearby properties",
    group: "Uplisting — Properties",
    description: "Find Uplisting properties near a given location."
  },
  {
    name: "uplisting_get_reviews",
    label: "Get reviews",
    group: "Uplisting — Properties",
    description: "Retrieve guest reviews for a property."
  },
  {
    name: "uplisting_get_channel",
    label: "Get channel info",
    group: "Uplisting — Properties",
    description:
      "Get channel (Airbnb, Booking.com, etc.) details for a property."
  },
  {
    name: "uplisting_check_availability",
    label: "Check availability",
    group: "Uplisting — Bookings",
    description: "Check if a property is available for given dates."
  },
  {
    name: "uplisting_create_quote",
    write: true,
    label: "Create quote",
    group: "Uplisting — Bookings",
    description: "Create a booking quote with pricing for given dates."
  },
  {
    name: "uplisting_get_booking",
    label: "Get booking",
    group: "Uplisting — Bookings",
    description: "Retrieve details for a specific booking by ID."
  },
  {
    name: "uplisting_search_bookings",
    label: "Search bookings",
    group: "Uplisting — Bookings",
    description: "Search bookings by guest name, email, or date range."
  },
  {
    name: "uplisting_get_calendar",
    label: "Get calendar",
    group: "Uplisting — Bookings",
    description: "Retrieve the availability calendar for a property."
  },
  {
    name: "uplisting_get_financials",
    label: "Get financials",
    group: "Uplisting — Bookings",
    description: "Retrieve financial summary for a booking."
  },
  {
    name: "uplisting_update_booking_notes",
    write: true,
    label: "Update booking notes",
    group: "Uplisting — Bookings",
    description: "Add or update internal notes on a booking."
  },
  {
    name: "uplisting_get_guest",
    label: "Get guest",
    group: "Uplisting — Guests",
    description: "Retrieve guest profile details."
  },
  {
    name: "uplisting_get_guest_history",
    label: "Get guest history",
    group: "Uplisting — Guests",
    description: "Retrieve a guest's booking history across properties."
  },
  {
    name: "uplisting_send_message",
    write: true,
    label: "Send message to guest",
    group: "Uplisting — Guests",
    description: "Send a message to a guest via the booking platform."
  },

  // ── Payments (provider-agnostic: Stripe today; Clover, Square in development) ─────
  {
    name: "payment_create_link",
    write: true,
    label: "Create payment link",
    group: "Payments — Charges",
    description:
      "Generate a hosted checkout link and drop it in chat for the customer to pay."
  },
  {
    name: "payment_charge_stored_card",
    label: "Charge stored card",
    group: "Payments — Charges",
    description: "Off-session charge on a customer with a saved card token."
  },
  {
    name: "payment_refund",
    label: "Refund a payment",
    group: "Payments — Refunds",
    description:
      "Refund a previous Velaro-originated payment, full or partial. Site-scoped via PaymentEvents.",
    destructive: true
  },
  {
    name: "payment_get_status",
    label: "Get payment status",
    group: "Payments — Charges",
    description:
      "Check the status of a Velaro-originated payment (created, succeeded, failed, refunded)."
  },
  {
    name: "payment_send_invoice",
    write: true,
    label: "Send hosted invoice",
    group: "Payments — Invoicing",
    description:
      "Send a provider-hosted invoice with line items and a due date."
  },
  {
    name: "payment_create_recurring_schedule",
    write: true,
    label: "Set up recurring charge",
    group: "Payments — Recurring",
    description:
      "Weekly or monthly auto-charge on a saved customer (subscription / retainer)."
  },

  // ── Custom Commerce Connector (bring-your-own-endpoint, e.g. Laravel/Bagisto/Lunar/custom stacks) ─
  {
    name: "custom_commerce_call_endpoint",
    label: "Call custom commerce endpoint",
    group: "Custom Commerce — Bespoke",
    description:
      "Calls a named endpoint your team registered (cart, order lookup, quote request, etc.) and maps the response into the conversation."
  },

  // ── Call Attribution (CallRail + native tracking numbers) ───────────────────────
  {
    name: "get_call_attribution_data",
    label: "Get call attribution data",
    group: "CallRail — Reporting",
    description:
      "Returns recent inbound-call marketing attribution (source, campaign, medium) from CallRail and native tracking numbers."
  },
  {
    name: "tag_number_with_campaign",
    write: true,
    label: "Tag number with campaign",
    group: "CallRail — Reporting",
    description:
      "Assigns or clears a campaign label on one of the site's phone numbers so inbound calls are attributed to that campaign."
  },

  // ── Copilot Studio (Microsoft DirectLine + Azure AI Agent Service) ──────────────
  {
    name: "copilot_studio_query",
    label: "Ask Copilot Studio Agent",
    group: "Copilot Studio — Core",
    description:
      "Queries the customer's connected Microsoft Copilot Studio agent or Azure AI Agent for an answer, using the site's configured knowledge-source routing."
  },

  // ── Recruiting Bot (jobs feed sync + candidate intake) ───────────────────────────
  {
    name: "recruiting_search_jobs",
    label: "Search job listings",
    group: "Recruiting — Jobs",
    description:
      "Searches the company's synced open positions by keyword, department, or location."
  },
  {
    name: "recruiting_submit_application",
    write: true,
    label: "Submit job application",
    group: "Recruiting — Candidates",
    description:
      "Records a candidate's application info and emails the site's configured recruiter."
  },

  // ── Velaro CRM (native, gated by EnableNativeCrm) ────────────────────────────────
  {
    name: "native_crm_search_contacts",
    label: "Search contacts",
    group: "Velaro CRM — Contacts",
    description: "Searches CRM contacts by name, email, or phone."
  },
  {
    name: "native_crm_get_contact",
    label: "Get contact",
    group: "Velaro CRM — Contacts",
    description: "Fetches a single CRM contact record."
  },
  {
    name: "native_crm_create_contact",
    label: "Create contact",
    group: "Velaro CRM — Contacts",
    description: "Creates a new CRM contact.",
    write: true
  },
  {
    name: "native_crm_update_contact",
    label: "Update contact",
    group: "Velaro CRM — Contacts",
    description: "Updates fields on an existing CRM contact.",
    write: true
  },
  {
    name: "native_crm_get_timeline",
    label: "Get contact timeline",
    group: "Velaro CRM — Contacts",
    description: "Returns the activity/interaction timeline for a contact."
  },
  {
    name: "native_crm_get_tickets",
    label: "Get contact tickets",
    group: "Velaro CRM — Contacts",
    description: "Returns support tickets associated with a contact."
  },
  {
    name: "native_crm_search_companies",
    label: "Search companies",
    group: "Velaro CRM — Companies",
    description: "Searches CRM companies by name or domain."
  },
  {
    name: "native_crm_get_company",
    label: "Get company",
    group: "Velaro CRM — Companies",
    description: "Fetches a single CRM company record."
  },
  {
    name: "native_crm_create_company",
    label: "Create company",
    group: "Velaro CRM — Companies",
    description: "Creates a new CRM company.",
    write: true
  },
  {
    name: "native_crm_update_company",
    label: "Update company",
    group: "Velaro CRM — Companies",
    description: "Updates fields on an existing CRM company.",
    write: true
  },
  {
    name: "native_crm_search_deals",
    label: "Search deals",
    group: "Velaro CRM — Deals",
    description: "Searches CRM deals by name, stage, or contact."
  },
  {
    name: "native_crm_get_deal",
    label: "Get deal",
    group: "Velaro CRM — Deals",
    description: "Fetches a single CRM deal record."
  },
  {
    name: "native_crm_create_deal",
    label: "Create deal",
    group: "Velaro CRM — Deals",
    description: "Creates a new CRM deal.",
    write: true
  },
  {
    name: "native_crm_update_deal",
    label: "Update deal",
    group: "Velaro CRM — Deals",
    description: "Updates fields on an existing CRM deal, including stage.",
    write: true
  },
  {
    name: "native_crm_assign_deal",
    label: "Assign deal",
    group: "Velaro CRM — Deals",
    description: "Assigns a CRM deal to an owner/agent.",
    write: true
  },
  {
    name: "native_crm_link_contact_to_deal",
    label: "Link contact to deal",
    group: "Velaro CRM — Deals",
    description: "Associates an existing contact with a deal.",
    write: true
  },
  {
    name: "native_crm_get_deal_contacts",
    label: "Get deal contacts",
    group: "Velaro CRM — Deals",
    description: "Returns contacts associated with a deal."
  },
  {
    name: "native_crm_get_deal_activities",
    label: "Get deal activities",
    group: "Velaro CRM — Deals",
    description: "Returns the activity log for a deal."
  },
  {
    name: "native_crm_get_pipelines",
    label: "Get pipelines",
    group: "Velaro CRM — Deals",
    description: "Returns configured CRM pipelines and stages."
  },
  {
    name: "native_crm_add_line_item",
    label: "Add deal line item",
    group: "Velaro CRM — Deals",
    description: "Adds a product/line item to a CRM deal.",
    write: true
  },
  {
    name: "native_crm_get_deal_line_items",
    label: "Get deal line items",
    group: "Velaro CRM — Deals",
    description: "Returns line items on a CRM deal."
  },
  {
    name: "native_crm_search_products",
    label: "Search CRM products",
    group: "Velaro CRM — Deals",
    description: "Searches the CRM product catalog for line-item lookup."
  },
  {
    name: "native_crm_add_note",
    label: "Add deal note",
    group: "Velaro CRM — Activity",
    description: "Adds a note to a CRM deal.",
    write: true
  },
  {
    name: "native_crm_get_notes",
    label: "Get deal notes",
    group: "Velaro CRM — Activity",
    description: "Returns notes logged on a CRM deal."
  },
  {
    name: "native_crm_log_activity",
    label: "Log CRM activity",
    group: "Velaro CRM — Activity",
    description: "Logs a call/email/meeting activity against a CRM record.",
    write: true
  },
  {
    name: "native_crm_get_activities",
    label: "Get CRM activities",
    group: "Velaro CRM — Activity",
    description: "Returns logged activities for a CRM record."
  },
  {
    name: "native_crm_complete_activity",
    label: "Complete activity",
    group: "Velaro CRM — Activity",
    description: "Marks a scheduled CRM activity/task complete.",
    write: true
  },
  {
    name: "native_crm_create_ticket",
    label: "Create support ticket",
    group: "Velaro CRM — Tickets",
    description: "Creates a new CRM support ticket.",
    write: true
  },
  {
    name: "native_crm_update_ticket",
    label: "Update support ticket",
    group: "Velaro CRM — Tickets",
    description: "Updates status/fields on an existing CRM support ticket.",
    write: true
  },
  {
    name: "native_crm_get_buying_signals",
    label: "Get buying signals",
    group: "Velaro CRM — Insights",
    description:
      "Returns AI-detected buying-signal insights for a contact or deal."
  },
  {
    name: "native_crm_suggest_next_action",
    label: "Suggest next action",
    group: "Velaro CRM — Insights",
    description:
      "Returns an AI-suggested next best action for a deal or contact."
  },

  // Klaviyo-parity Gap 1+2/3 (2026-08-26) — real-store-agnostic ecommerce intelligence skills.
  // Both ride EnableEcommerceSync (no dedicated integration flag), computed from EcommerceOrder/
  // EcommerceContact data synced from any connected store (Shopify/WooCommerce/Magento/Square).
  {
    name: "ecommerce_get_rfm_segment",
    label: "Customer RFM segment",
    group: "Ecommerce Intelligence — Segmentation",
    description:
      "Returns a contact's Recency/Frequency/Monetary segment (e.g. Champion, At Risk) and heuristic churn/LTV predictions from real order history."
  },
  {
    name: "ecommerce_get_recommendations",
    label: "Product recommendations",
    group: "Ecommerce Intelligence — Recommendations",
    description:
      "Returns co-purchase-based product recommendations for a contact, falling back to store best-sellers with no order history."
  },

  // Klaviyo-parity "build a segment" / "enroll in campaign" gaps (2026-08-27) — agents only.
  {
    name: "audience_build_segment",
    label: "Build contact segment",
    group: "Ecommerce Intelligence — Audiences",
    description:
      "Creates or updates a saved ContactSegment from structured conditions on email, name, phone, tags, or source (AND-only). Re-running with the same name updates instead of duplicating."
  },
  {
    name: "campaign_enroll_contact",
    label: "Enroll contact in campaign",
    group: "Ecommerce Intelligence — Audiences",
    description:
      "Enrolls one contact by email into an existing, admin-authored email campaign by name. Never enrolls more than one contact per call."
  }
];

export default ALL_SKILL_DEFS;
